import { Contract } from '@ethersproject/contracts';
import { Interface } from '@ethersproject/abi';
import { parseUnits } from '@ethersproject/units';
import { Provider } from '@ethersproject/abstract-provider';
import { Signer } from '@ethersproject/abstract-signer';
import { isAddress } from '@ethersproject/address';
import { utils, computeUserWinningPicksForRandomNumber, encodeWinningPicks } from '@pooltogether/v4-utils-js';
export { calculate, compute, utils } from '@pooltogether/v4-utils-js';
import { BigNumber, ethers } from 'ethers';
import { contract, batch } from '@pooltogether/etherplex';
import { deserializeBigNumbers, NETWORK } from '@pooltogether/utilities';
import { MaxUint256 } from '@ethersproject/constants';
import { signERC2612Permit } from 'eth-permit';
import { signData } from 'eth-permit/dist/rpc';

var DAYS_IN_SECONDS = 86400;
var HOUR_IN_SECONDS = 3600;
var TIER_DENOMINATION = 9;
/**
 * A shortlist of PoolTogether v4 contract names that are used by the classes in this repo.
 */

var ContractType;

(function (ContractType) {
  ContractType["YieldSourcePrizePool"] = "YieldSourcePrizePool";
  ContractType["Ticket"] = "Ticket";
  ContractType["Token"] = "Token";
  ContractType["PrizeDistributor"] = "PrizeDistributor";
  ContractType["DrawBuffer"] = "DrawBuffer";
  ContractType["DrawBeacon"] = "DrawBeacon";
  ContractType["DrawCalculator"] = "DrawCalculator";
  ContractType["DrawCalculatorTimelock"] = "DrawCalculatorTimelock";
  ContractType["PrizeDistributionBuffer"] = "PrizeDistributionBuffer";
  ContractType["PrizeDistributionFactory"] = "PrizeDistributionFactory";
  ContractType["PrizeTierHistory"] = "PrizeTierHistory";
  ContractType["EIP2612PermitAndDeposit"] = "EIP2612PermitAndDeposit"; // ... more contract types
})(ContractType || (ContractType = {}));

var PrizeApiStatus;

(function (PrizeApiStatus) {
  PrizeApiStatus["loading"] = "LOADING";
  PrizeApiStatus["success"] = "SUCCESS";
  PrizeApiStatus["failure"] = "FAILURE";
})(PrizeApiStatus || (PrizeApiStatus = {}));

var createContract = function createContract(address, contractInterface, provider) {
  return new Contract(address, contractInterface, provider);
};

function createInterface(abi) {
  return new Interface(abi);
}

function formatTierToBasePercentage(distribution) {
  return parseUnits(distribution, TIER_DENOMINATION);
}

function getContractListChainIds(contracts) {
  return Array.from(new Set(contracts.map(function (c) {
    return c.chainId;
  })));
}

function getContractsByType(contracts, type) {
  return contracts.filter(function (contract) {
    return contract.type === type;
  });
}

function sortContractsByChainId(contracts) {
  var sortedContracts = {};
  var chainIds = new Set(contracts.map(function (c) {
    return c.chainId;
  }));
  chainIds.forEach(function (chainId) {
    var filteredContracts = contracts.filter(function (c) {
      return c.chainId === chainId;
    });
    sortedContracts[chainId] = filteredContracts;
  });
  return sortedContracts;
}

/**
 * Reads the contract list and pulls out connected contracts based on the
 * children extension.
 *
 * NOTE: This extension is added in the intialize functions for creating the instances of
 * PrizePoolNetwork and PrizeDistributors
 */

function sortContractsByContractTypeAndChildren(contracts, contractType) {
  var prizePoolContracts = getContractsByType(contracts, contractType);
  return prizePoolContracts.map(function (prizePoolContract) {
    return [prizePoolContract].concat(findChildContracts(prizePoolContract, contracts));
  });
}

function findChildContracts(parentContract, contracts) {
  var _parentContract$exten;

  var children = (_parentContract$exten = parentContract.extensions) == null ? void 0 : _parentContract$exten.children;
  if (!children) return [];
  if (!Array.isArray(children)) throw new Error('Invalid children extension');
  var childContracts = [];
  children.forEach(function (childIdentifier) {
    var childContract = contracts.find(function (contract) {
      return isMatchingContractIdentifier(childIdentifier, contract);
    });
    if (childContract) childContracts.push(childContract);
  });
  return childContracts;
}

function isMatchingContractIdentifier(contractIdentifier, contract) {
  return contractIdentifier.address === contract.address && contractIdentifier.chainId === contract.chainId;
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;

  _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function createCommonjsModule(fn, module) {
	return module = { exports: {} }, fn(module, module.exports), module.exports;
}

var runtime_1 = createCommonjsModule(function (module) {
/**
 * Copyright (c) 2014-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var runtime = (function (exports) {

  var Op = Object.prototype;
  var hasOwn = Op.hasOwnProperty;
  var undefined$1; // More compressible than void 0.
  var $Symbol = typeof Symbol === "function" ? Symbol : {};
  var iteratorSymbol = $Symbol.iterator || "@@iterator";
  var asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator";
  var toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
    return obj[key];
  }
  try {
    // IE 8 has a broken Object.defineProperty that only works on DOM objects.
    define({}, "");
  } catch (err) {
    define = function(obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    // If outerFn provided and outerFn.prototype is a Generator, then outerFn.prototype instanceof Generator.
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator;
    var generator = Object.create(protoGenerator.prototype);
    var context = new Context(tryLocsList || []);

    // The ._invoke method unifies the implementations of the .next,
    // .throw, and .return methods.
    generator._invoke = makeInvokeMethod(innerFn, self, context);

    return generator;
  }
  exports.wrap = wrap;

  // Try/catch helper to minimize deoptimizations. Returns a completion
  // record like context.tryEntries[i].completion. This interface could
  // have been (and was previously) designed to take a closure to be
  // invoked without arguments, but in all the cases we care about we
  // already have an existing method we want to call, so there's no need
  // to create a new function object. We can even get away with assuming
  // the method takes exactly one argument, since that happens to be true
  // in every case, so we don't have to touch the arguments object. The
  // only additional allocation required is the completion record, which
  // has a stable shape and so hopefully should be cheap to allocate.
  function tryCatch(fn, obj, arg) {
    try {
      return { type: "normal", arg: fn.call(obj, arg) };
    } catch (err) {
      return { type: "throw", arg: err };
    }
  }

  var GenStateSuspendedStart = "suspendedStart";
  var GenStateSuspendedYield = "suspendedYield";
  var GenStateExecuting = "executing";
  var GenStateCompleted = "completed";

  // Returning this object from the innerFn has the same effect as
  // breaking out of the dispatch switch statement.
  var ContinueSentinel = {};

  // Dummy constructor functions that we use as the .constructor and
  // .constructor.prototype properties for functions that return Generator
  // objects. For full spec compliance, you may wish to configure your
  // minifier not to mangle the names of these two functions.
  function Generator() {}
  function GeneratorFunction() {}
  function GeneratorFunctionPrototype() {}

  // This is a polyfill for %IteratorPrototype% for environments that
  // don't natively support it.
  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });

  var getProto = Object.getPrototypeOf;
  var NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  if (NativeIteratorPrototype &&
      NativeIteratorPrototype !== Op &&
      hasOwn.call(NativeIteratorPrototype, iteratorSymbol)) {
    // This environment has a native %IteratorPrototype%; use it instead
    // of the polyfill.
    IteratorPrototype = NativeIteratorPrototype;
  }

  var Gp = GeneratorFunctionPrototype.prototype =
    Generator.prototype = Object.create(IteratorPrototype);
  GeneratorFunction.prototype = GeneratorFunctionPrototype;
  define(Gp, "constructor", GeneratorFunctionPrototype);
  define(GeneratorFunctionPrototype, "constructor", GeneratorFunction);
  GeneratorFunction.displayName = define(
    GeneratorFunctionPrototype,
    toStringTagSymbol,
    "GeneratorFunction"
  );

  // Helper for defining the .next, .throw, and .return methods of the
  // Iterator interface in terms of a single ._invoke method.
  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function(method) {
      define(prototype, method, function(arg) {
        return this._invoke(method, arg);
      });
    });
  }

  exports.isGeneratorFunction = function(genFun) {
    var ctor = typeof genFun === "function" && genFun.constructor;
    return ctor
      ? ctor === GeneratorFunction ||
        // For the native GeneratorFunction constructor, the best we can
        // do is to check its .name property.
        (ctor.displayName || ctor.name) === "GeneratorFunction"
      : false;
  };

  exports.mark = function(genFun) {
    if (Object.setPrototypeOf) {
      Object.setPrototypeOf(genFun, GeneratorFunctionPrototype);
    } else {
      genFun.__proto__ = GeneratorFunctionPrototype;
      define(genFun, toStringTagSymbol, "GeneratorFunction");
    }
    genFun.prototype = Object.create(Gp);
    return genFun;
  };

  // Within the body of any async function, `await x` is transformed to
  // `yield regeneratorRuntime.awrap(x)`, so that the runtime can test
  // `hasOwn.call(value, "__await")` to determine if the yielded value is
  // meant to be awaited.
  exports.awrap = function(arg) {
    return { __await: arg };
  };

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);
      if (record.type === "throw") {
        reject(record.arg);
      } else {
        var result = record.arg;
        var value = result.value;
        if (value &&
            typeof value === "object" &&
            hasOwn.call(value, "__await")) {
          return PromiseImpl.resolve(value.__await).then(function(value) {
            invoke("next", value, resolve, reject);
          }, function(err) {
            invoke("throw", err, resolve, reject);
          });
        }

        return PromiseImpl.resolve(value).then(function(unwrapped) {
          // When a yielded Promise is resolved, its final value becomes
          // the .value of the Promise<{value,done}> result for the
          // current iteration.
          result.value = unwrapped;
          resolve(result);
        }, function(error) {
          // If a rejected Promise was yielded, throw the rejection back
          // into the async generator function so it can be handled there.
          return invoke("throw", error, resolve, reject);
        });
      }
    }

    var previousPromise;

    function enqueue(method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function(resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise =
        // If enqueue has been called before, then we want to wait until
        // all previous Promises have been resolved before calling invoke,
        // so that results are always delivered in the correct order. If
        // enqueue has not been called before, then it is important to
        // call invoke immediately, without waiting on a callback to fire,
        // so that the async generator function has the opportunity to do
        // any necessary setup in a predictable way. This predictability
        // is why the Promise constructor synchronously invokes its
        // executor callback, and why async functions synchronously
        // execute code before the first await. Since we implement simple
        // async functions in terms of async generators, it is especially
        // important to get this right, even though it requires care.
        previousPromise ? previousPromise.then(
          callInvokeWithMethodAndArg,
          // Avoid propagating failures to Promises returned by later
          // invocations of the iterator.
          callInvokeWithMethodAndArg
        ) : callInvokeWithMethodAndArg();
    }

    // Define the unified helper method that is used to implement .next,
    // .throw, and .return (see defineIteratorMethods).
    this._invoke = enqueue;
  }

  defineIteratorMethods(AsyncIterator.prototype);
  define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  });
  exports.AsyncIterator = AsyncIterator;

  // Note that simple async functions are implemented on top of
  // AsyncIterator objects; they just return a Promise for the value of
  // the final result produced by the iterator.
  exports.async = function(innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    if (PromiseImpl === void 0) PromiseImpl = Promise;

    var iter = new AsyncIterator(
      wrap(innerFn, outerFn, self, tryLocsList),
      PromiseImpl
    );

    return exports.isGeneratorFunction(outerFn)
      ? iter // If outerFn is a generator, return the full iterator.
      : iter.next().then(function(result) {
          return result.done ? result.value : iter.next();
        });
  };

  function makeInvokeMethod(innerFn, self, context) {
    var state = GenStateSuspendedStart;

    return function invoke(method, arg) {
      if (state === GenStateExecuting) {
        throw new Error("Generator is already running");
      }

      if (state === GenStateCompleted) {
        if (method === "throw") {
          throw arg;
        }

        // Be forgiving, per 25.3.3.3.3 of the spec:
        // https://people.mozilla.org/~jorendorff/es6-draft.html#sec-generatorresume
        return doneResult();
      }

      context.method = method;
      context.arg = arg;

      while (true) {
        var delegate = context.delegate;
        if (delegate) {
          var delegateResult = maybeInvokeDelegate(delegate, context);
          if (delegateResult) {
            if (delegateResult === ContinueSentinel) continue;
            return delegateResult;
          }
        }

        if (context.method === "next") {
          // Setting context._sent for legacy support of Babel's
          // function.sent implementation.
          context.sent = context._sent = context.arg;

        } else if (context.method === "throw") {
          if (state === GenStateSuspendedStart) {
            state = GenStateCompleted;
            throw context.arg;
          }

          context.dispatchException(context.arg);

        } else if (context.method === "return") {
          context.abrupt("return", context.arg);
        }

        state = GenStateExecuting;

        var record = tryCatch(innerFn, self, context);
        if (record.type === "normal") {
          // If an exception is thrown from innerFn, we leave state ===
          // GenStateExecuting and loop back for another invocation.
          state = context.done
            ? GenStateCompleted
            : GenStateSuspendedYield;

          if (record.arg === ContinueSentinel) {
            continue;
          }

          return {
            value: record.arg,
            done: context.done
          };

        } else if (record.type === "throw") {
          state = GenStateCompleted;
          // Dispatch the exception by looping back around to the
          // context.dispatchException(context.arg) call above.
          context.method = "throw";
          context.arg = record.arg;
        }
      }
    };
  }

  // Call delegate.iterator[context.method](context.arg) and handle the
  // result, either by returning a { value, done } result from the
  // delegate iterator, or by modifying context.method and context.arg,
  // setting context.delegate to null, and returning the ContinueSentinel.
  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];
    if (method === undefined$1) {
      // A .throw or .return when the delegate iterator has no .throw
      // method always terminates the yield* loop.
      context.delegate = null;

      if (context.method === "throw") {
        // Note: ["return"] must be used for ES3 parsing compatibility.
        if (delegate.iterator["return"]) {
          // If the delegate iterator has a return method, give it a
          // chance to clean up.
          context.method = "return";
          context.arg = undefined$1;
          maybeInvokeDelegate(delegate, context);

          if (context.method === "throw") {
            // If maybeInvokeDelegate(context) changed context.method from
            // "return" to "throw", let that override the TypeError below.
            return ContinueSentinel;
          }
        }

        context.method = "throw";
        context.arg = new TypeError(
          "The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);

    if (record.type === "throw") {
      context.method = "throw";
      context.arg = record.arg;
      context.delegate = null;
      return ContinueSentinel;
    }

    var info = record.arg;

    if (! info) {
      context.method = "throw";
      context.arg = new TypeError("iterator result is not an object");
      context.delegate = null;
      return ContinueSentinel;
    }

    if (info.done) {
      // Assign the result of the finished delegate to the temporary
      // variable specified by delegate.resultName (see delegateYield).
      context[delegate.resultName] = info.value;

      // Resume execution at the desired location (see delegateYield).
      context.next = delegate.nextLoc;

      // If context.method was "throw" but the delegate handled the
      // exception, let the outer generator proceed normally. If
      // context.method was "next", forget context.arg since it has been
      // "consumed" by the delegate iterator. If context.method was
      // "return", allow the original .return call to continue in the
      // outer generator.
      if (context.method !== "return") {
        context.method = "next";
        context.arg = undefined$1;
      }

    } else {
      // Re-yield the result returned by the delegate method.
      return info;
    }

    // The delegate iterator is finished, so forget it and continue with
    // the outer generator.
    context.delegate = null;
    return ContinueSentinel;
  }

  // Define Generator.prototype.{next,throw,return} in terms of the
  // unified ._invoke helper method.
  defineIteratorMethods(Gp);

  define(Gp, toStringTagSymbol, "Generator");

  // A Generator should always return itself as the iterator object when the
  // @@iterator function is called on it. Some browsers' implementations of the
  // iterator prototype chain incorrectly implement this, causing the Generator
  // object to not be returned from this call. This ensures that doesn't happen.
  // See https://github.com/facebook/regenerator/issues/274 for more details.
  define(Gp, iteratorSymbol, function() {
    return this;
  });

  define(Gp, "toString", function() {
    return "[object Generator]";
  });

  function pushTryEntry(locs) {
    var entry = { tryLoc: locs[0] };

    if (1 in locs) {
      entry.catchLoc = locs[1];
    }

    if (2 in locs) {
      entry.finallyLoc = locs[2];
      entry.afterLoc = locs[3];
    }

    this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal";
    delete record.arg;
    entry.completion = record;
  }

  function Context(tryLocsList) {
    // The root entry object (effectively a try statement without a catch
    // or a finally block) gives us a place to store values thrown from
    // locations where there is no enclosing try statement.
    this.tryEntries = [{ tryLoc: "root" }];
    tryLocsList.forEach(pushTryEntry, this);
    this.reset(true);
  }

  exports.keys = function(object) {
    var keys = [];
    for (var key in object) {
      keys.push(key);
    }
    keys.reverse();

    // Rather than returning an object with a next method, we keep
    // things simple and return the next function itself.
    return function next() {
      while (keys.length) {
        var key = keys.pop();
        if (key in object) {
          next.value = key;
          next.done = false;
          return next;
        }
      }

      // To avoid creating an additional object, we just hang the .value
      // and .done properties off the next function object itself. This
      // also ensures that the minifier will not anonymize the function.
      next.done = true;
      return next;
    };
  };

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) {
        return iteratorMethod.call(iterable);
      }

      if (typeof iterable.next === "function") {
        return iterable;
      }

      if (!isNaN(iterable.length)) {
        var i = -1, next = function next() {
          while (++i < iterable.length) {
            if (hasOwn.call(iterable, i)) {
              next.value = iterable[i];
              next.done = false;
              return next;
            }
          }

          next.value = undefined$1;
          next.done = true;

          return next;
        };

        return next.next = next;
      }
    }

    // Return an iterator with no values.
    return { next: doneResult };
  }
  exports.values = values;

  function doneResult() {
    return { value: undefined$1, done: true };
  }

  Context.prototype = {
    constructor: Context,

    reset: function(skipTempReset) {
      this.prev = 0;
      this.next = 0;
      // Resetting context._sent for legacy support of Babel's
      // function.sent implementation.
      this.sent = this._sent = undefined$1;
      this.done = false;
      this.delegate = null;

      this.method = "next";
      this.arg = undefined$1;

      this.tryEntries.forEach(resetTryEntry);

      if (!skipTempReset) {
        for (var name in this) {
          // Not sure about the optimal order of these conditions:
          if (name.charAt(0) === "t" &&
              hasOwn.call(this, name) &&
              !isNaN(+name.slice(1))) {
            this[name] = undefined$1;
          }
        }
      }
    },

    stop: function() {
      this.done = true;

      var rootEntry = this.tryEntries[0];
      var rootRecord = rootEntry.completion;
      if (rootRecord.type === "throw") {
        throw rootRecord.arg;
      }

      return this.rval;
    },

    dispatchException: function(exception) {
      if (this.done) {
        throw exception;
      }

      var context = this;
      function handle(loc, caught) {
        record.type = "throw";
        record.arg = exception;
        context.next = loc;

        if (caught) {
          // If the dispatched exception was caught by a catch block,
          // then let that catch block handle the exception normally.
          context.method = "next";
          context.arg = undefined$1;
        }

        return !! caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        var record = entry.completion;

        if (entry.tryLoc === "root") {
          // Exception thrown outside of any try block that could handle
          // it, so set the completion value of the entire function to
          // throw the exception.
          return handle("end");
        }

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc");
          var hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            } else if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) {
              return handle(entry.catchLoc, true);
            }

          } else if (hasFinally) {
            if (this.prev < entry.finallyLoc) {
              return handle(entry.finallyLoc);
            }

          } else {
            throw new Error("try statement without catch or finally");
          }
        }
      }
    },

    abrupt: function(type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc <= this.prev &&
            hasOwn.call(entry, "finallyLoc") &&
            this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      if (finallyEntry &&
          (type === "break" ||
           type === "continue") &&
          finallyEntry.tryLoc <= arg &&
          arg <= finallyEntry.finallyLoc) {
        // Ignore the finally entry if control is not jumping to a
        // location outside the try/catch block.
        finallyEntry = null;
      }

      var record = finallyEntry ? finallyEntry.completion : {};
      record.type = type;
      record.arg = arg;

      if (finallyEntry) {
        this.method = "next";
        this.next = finallyEntry.finallyLoc;
        return ContinueSentinel;
      }

      return this.complete(record);
    },

    complete: function(record, afterLoc) {
      if (record.type === "throw") {
        throw record.arg;
      }

      if (record.type === "break" ||
          record.type === "continue") {
        this.next = record.arg;
      } else if (record.type === "return") {
        this.rval = this.arg = record.arg;
        this.method = "return";
        this.next = "end";
      } else if (record.type === "normal" && afterLoc) {
        this.next = afterLoc;
      }

      return ContinueSentinel;
    },

    finish: function(finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) {
          this.complete(entry.completion, entry.afterLoc);
          resetTryEntry(entry);
          return ContinueSentinel;
        }
      }
    },

    "catch": function(tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;
          if (record.type === "throw") {
            var thrown = record.arg;
            resetTryEntry(entry);
          }
          return thrown;
        }
      }

      // The context.catch method must only be called with a location
      // argument that corresponds to a known catch block.
      throw new Error("illegal catch attempt");
    },

    delegateYield: function(iterable, resultName, nextLoc) {
      this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      };

      if (this.method === "next") {
        // Deliberately forget the last sent value so that we don't
        // accidentally pass it on to the delegate.
        this.arg = undefined$1;
      }

      return ContinueSentinel;
    }
  };

  // Regardless of whether this script is executing as a CommonJS module
  // or not, return the runtime object so that we can declare the variable
  // regeneratorRuntime in the outer scope, which allows this module to be
  // injected easily by `bin/regenerator --include-runtime script.js`.
  return exports;

}(
  // If this script is executing as a CommonJS module, use module.exports
  // as the regeneratorRuntime namespace. Otherwise create a new empty
  // object. Either way, the resulting object will be used to initialize
  // the regeneratorRuntime variable at the top of this file.
   module.exports 
));

try {
  regeneratorRuntime = runtime;
} catch (accidentalStrictMode) {
  // This module should not be running in strict mode, so the above
  // assignment should always work unless something is misconfigured. Just
  // in case runtime.js accidentally runs in strict mode, in modern engines
  // we can explicitly access globalThis. In older engines we can escape
  // strict mode using a global Function call. This could conceivably fail
  // if a Content Security Policy forbids using Function, but in that case
  // the proper solution is to fix the accidental strict mode problem. If
  // you've misconfigured your bundler to force strict mode and applied a
  // CSP to forbid Function, and you're not willing to fix either of those
  // problems, please detail your unique predicament in a GitHub issue.
  if (typeof globalThis === "object") {
    globalThis.regeneratorRuntime = runtime;
  } else {
    Function("r", "regeneratorRuntime = r")(runtime);
  }
}
});

function getTokenData(_x) {
  return _getTokenData.apply(this, arguments);
}

function _getTokenData() {
  _getTokenData = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(tokenContract) {
    var namePromise, symbolPromise, decimalsPromise, results;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            namePromise = tokenContract.functions.name();
            symbolPromise = tokenContract.functions.symbol();
            decimalsPromise = tokenContract.functions.decimals();
            _context.next = 5;
            return Promise.all([namePromise, symbolPromise, decimalsPromise]);

          case 5:
            results = _context.sent;
            return _context.abrupt("return", {
              name: results[0][0],
              symbol: results[1][0],
              decimals: results[2][0]
            });

          case 7:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _getTokenData.apply(this, arguments);
}

function getUsersERC20Balance(_x2, _x3) {
  return _getUsersERC20Balance.apply(this, arguments);
}

function _getUsersERC20Balance() {
  _getUsersERC20Balance = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(usersAddress, tokenContract) {
    var result;
    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return tokenContract.functions.balanceOf(usersAddress);

          case 2:
            result = _context2.sent;
            return _context2.abrupt("return", result[0]);

          case 4:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _getUsersERC20Balance.apply(this, arguments);
}

function getUsersTokenAllowance(_x4, _x5, _x6) {
  return _getUsersTokenAllowance.apply(this, arguments);
}

function _getUsersTokenAllowance() {
  _getUsersTokenAllowance = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(usersAddress, spendersAddress, tokenContract) {
    var result;
    return runtime_1.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _context3.next = 2;
            return tokenContract.functions.allowance(usersAddress, spendersAddress);

          case 2:
            result = _context3.sent;
            return _context3.abrupt("return", result[0]);

          case 4:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));
  return _getUsersTokenAllowance.apply(this, arguments);
}

/**
 * Throws an error if the provided address is invalid.
 * @param errorPrefix the class and function name of where the error occurred
 * @param address the address to validate
 */

function validateAddress(errorPrefix, address) {
  var isValidAddress = isAddress(address);

  if (!isValidAddress) {
    throw new Error(errorPrefix + (" | Invalid address: '" + address + "'"));
  }
}
/**
 * Throws an error if the Signer provided is not on the chain id provided.
 * @param errorPrefix the class and function name of where the error occurred
 * @param signer a Signer to validate
 * @param chainId the network to check for
 */

function validateSignerNetwork(_x, _x2, _x3) {
  return _validateSignerNetwork.apply(this, arguments);
}
/**
 * Throws an error if the signerOrProvider is not a Signer
 * @param errorPrefix the class and function name of where the error occurred
 * @param signerOrProvider a Signer or Provider to check
 */

function _validateSignerNetwork() {
  _validateSignerNetwork = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(errorPrefix, signer, chainId) {
    var signerChainId;
    return runtime_1.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return signer.getChainId();

          case 2:
            signerChainId = _context.sent;

            if (!(signerChainId !== chainId)) {
              _context.next = 5;
              break;
            }

            throw new Error(errorPrefix + (" | Signer is on network " + signerChainId + ". Expected network " + chainId));

          case 5:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));
  return _validateSignerNetwork.apply(this, arguments);
}

function validateIsSigner(errorPrefix, signerOrProvider) {
  if (!Signer.isSigner(signerOrProvider)) {
    throw new Error(errorPrefix + ' | signerOrProvider is not a signer');
  }
}
/**
 * Throws and error if the Signer or Provider is not on the chain id provided.
 * @param errorPrefix the class and function name of where the error occurred
 * @param signerOrProvider a Signer or Provider to check
 * @param chainId the network to check for
 */

function validateSignerOrProviderNetwork(_x4, _x5, _x6) {
  return _validateSignerOrProviderNetwork.apply(this, arguments);
}

function _validateSignerOrProviderNetwork() {
  _validateSignerOrProviderNetwork = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(errorPrefix, signerOrProvider, chainId) {
    var signerOrProviderChainId, _yield$signerOrProvid, providerChainId;

    return runtime_1.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            if (!Provider.isProvider(signerOrProvider)) {
              _context2.next = 8;
              break;
            }

            _context2.next = 3;
            return signerOrProvider.getNetwork();

          case 3:
            _yield$signerOrProvid = _context2.sent;
            providerChainId = _yield$signerOrProvid.chainId;
            signerOrProviderChainId = providerChainId;
            _context2.next = 12;
            break;

          case 8:
            if (!Signer.isSigner(signerOrProvider)) {
              _context2.next = 12;
              break;
            }

            _context2.next = 11;
            return signerOrProvider.getChainId();

          case 11:
            signerOrProviderChainId = _context2.sent;

          case 12:
            if (signerOrProviderChainId) {
              _context2.next = 16;
              break;
            }

            throw new Error(errorPrefix + " | Invalid value for signerOrProvider");

          case 16:
            if (!(signerOrProviderChainId !== chainId)) {
              _context2.next = 18;
              break;
            }

            throw new Error(errorPrefix + (" | Provider is on network " + signerOrProviderChainId + ". Expected network " + chainId));

          case 18:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _validateSignerOrProviderNetwork.apply(this, arguments);
}

/**
 * Finds a specific contract in the contract list and returns the metadata and ethers Contract.
 * If an addressOverride is supplied, it will be used to look up the contract in the metadata list. If it is not found, the first contract found in the list of that type regardless of version will be used.
 * @param chainId
 * @param signerOrProvider
 * @param contractType
 * @param contractMetadataList
 * @param addressOverride
 * @returns
 */

function getMetadataAndContract(chainId, signerOrProvider, contractType, contractMetadataList, addressOverride) {
  var contractMetadata;

  if (!!addressOverride) {
    contractMetadata = contractMetadataList.find(function (contract) {
      return contract.type === contractType && contract.chainId === chainId && contract.address === addressOverride;
    });
  } // Fallback for no addressOverride provided or found


  if (!contractMetadata) {
    contractMetadata = contractMetadataList.find(function (contract) {
      return contract.type === contractType && contract.chainId === chainId;
    });
  }

  if (!contractMetadata) {
    throw new Error("Invalid contract list. Missing " + contractType + ".");
  }

  var contract = createContract(addressOverride || contractMetadata.address, createInterface(contractMetadata.abi), signerOrProvider);
  return {
    contractMetadata: contractMetadata,
    contract: contract
  };
}

function createContractMetadata(chainId, address, type, abi, version, tags, extensions) {
  if (version === void 0) {
    version = {
      major: 1,
      minor: 0,
      patch: 0
    };
  }

  if (tags === void 0) {
    tags = [];
  }

  if (extensions === void 0) {
    extensions = {};
  }

  return {
    chainId: chainId,
    address: address,
    version: version,
    type: type,
    abi: abi,
    tags: tags,
    extensions: extensions
  };
}

var debug = /*#__PURE__*/require('debug')('v4-client-js');

/**
 * Searches through a contract list to find a particular contract keyed by chain id and address
 * @param contractList
 * @param chainId
 * @param address
 * @returns
 */
var findInContractList = function findInContractList(contractList, chainId, address) {
  return contractList.contracts.find(function (contract) {
    return contract.chainId === chainId && contract.address === address;
  });
};

/**
 * An ethers Contract Factory.
 * Given a ContractList, the ContractFactory will initialize ethers Contracts and easily provide Providers or Signers.
 */

var ContractFactory = /*#__PURE__*/function () {
  /**
   * Create an instance of a ContractFactory by providing Signers or Providers keyed by their chain ids and a list of contract metadata.
   * @constructor
   * @param signersOrProviders signers or providers keyed by their chain ids
   * @param contractList a list of contract metadata
   */
  function ContractFactory(signersOrProviders, contractList) {
    if (!signersOrProviders) throw new Error('signersOrProviders is required');
    if (!contractList) throw new Error('contractList is required');
    this.signersOrProviders = signersOrProviders;
    this.contractList = contractList;
    return this;
  }
  /**
   * Creates an ethers Contract for the contract identifier provided using the ContractList and Signers or Providers provided on initialization.
   * @param chainId the chain id the contract was deployed on
   * @param address the address of the contract to create
   * @returns an ethers contract for the provided contract identifier
   */


  var _proto = ContractFactory.prototype;

  _proto.getContract = function getContract(chainId, address) {
    var contract = findInContractList(this.contractList, chainId, address);
    debug('PoolTogetherV4:getContract', contract);

    if (!contract) {
      throw new Error("Contract not found for chainId: " + chainId + " and address: " + address);
    }

    return createContract(contract.address, createInterface(contract.abi), this.getSignerOrProvider(contract.chainId));
  }
  /**
   * Creates multiple ethers Contracts for the identifiers provided using the ContractList and Signers or Providers provided on initialization.
   * @param contractIdentifiers a list of unique identifiers for contracts to create
   * @returns a list of ethers contracts for the provided conract identifiers
   */
  ;

  _proto.getContracts = function getContracts(contractIdentifiers) {
    var _this = this;

    return contractIdentifiers.map(function (contractIdentifier) {
      return _this.getContract(contractIdentifier.chainId, contractIdentifier.address);
    });
  }
  /**
   * Gets a Signer or Provider for the chain id requested from the Signers or Providers prodiced on initialization.
   * @param chainId the chain id to get a signer or provider for
   * @returns the signer or provider for the chain id requested
   */
  ;

  _proto.getSignerOrProvider = function getSignerOrProvider(chainId) {
    return this.signersOrProviders[chainId];
  }
  /**
   * Getter for the Signers or Providers provided on initialization.
   * @returns the signers or providers the contract factory was initialized with
   */
  ;

  _proto.getSignersOrProviders = function getSignersOrProviders() {
    return this.signersOrProviders;
  }
  /**
   * Getter for the ContractList provided on initialization.
   * @returns the contract list the contract factory was initialized with
   */
  ;

  _proto.getContractList = function getContractList() {
    return this.contractList;
  };

  return ContractFactory;
}();

var ERC20Abi = [{
  constant: true,
  inputs: [],
  name: 'name',
  outputs: [{
    name: '',
    type: 'string'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'spender',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'approve',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'totalSupply',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'from',
    type: 'address'
  }, {
    name: 'to',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'transferFrom',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'decimals',
  outputs: [{
    name: '',
    type: 'uint8'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'spender',
    type: 'address'
  }, {
    name: 'addedValue',
    type: 'uint256'
  }],
  name: 'increaseAllowance',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'to',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'mint',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    name: 'owner',
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'symbol',
  outputs: [{
    name: '',
    type: 'string'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'account',
    type: 'address'
  }],
  name: 'addMinter',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'renounceMinter',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'spender',
    type: 'address'
  }, {
    name: 'subtractedValue',
    type: 'uint256'
  }],
  name: 'decreaseAllowance',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'to',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'transfer',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    name: 'account',
    type: 'address'
  }],
  name: 'isMinter',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'sender',
    type: 'address'
  }],
  name: 'initialize',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    name: 'owner',
    type: 'address'
  }, {
    name: 'spender',
    type: 'address'
  }],
  name: 'allowance',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'account',
    type: 'address'
  }],
  name: 'MinterAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'account',
    type: 'address'
  }],
  name: 'MinterRemoved',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    name: 'to',
    type: 'address'
  }, {
    indexed: false,
    name: 'value',
    type: 'uint256'
  }],
  name: 'Transfer',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'owner',
    type: 'address'
  }, {
    indexed: true,
    name: 'spender',
    type: 'address'
  }, {
    indexed: false,
    name: 'value',
    type: 'uint256'
  }],
  name: 'Approval',
  type: 'event'
}];

var createEmptyDrawResult = function createEmptyDrawResult(drawId) {
  return {
    drawId: drawId,
    totalValue: BigNumber.from(0),
    prizes: []
  };
};

var formatDrawResultsFromLegacyDrawResults = function formatDrawResultsFromLegacyDrawResults(LEGACY_drawResult) {
  var _prizes = LEGACY_drawResult.prizes;

  var prizes = _prizes.map(function (prize) {
    return {
      amount: BigNumber.from(prize.amount),
      tierIndex: 'distributionIndex' in prize ? prize.distributionIndex : prize.tierIndex,
      pick: BigNumber.from(prize.pick)
    };
  });

  var drawResults = {
    drawId: LEGACY_drawResult.drawId,
    totalValue: LEGACY_drawResult.totalValue,
    prizes: prizes
  };
  return drawResults;
};

var sumBigNumbers = function sumBigNumbers(values) {
  return values.reduce(function (sum, value) {
    return sum.add(value);
  }, BigNumber.from(0));
};

var formatDrawResultsFromPrizes = function formatDrawResultsFromPrizes(drawId, _allPrizes, maxPicksPerUser) {
  var prizes = _allPrizes.map(function (prize) {
    return {
      amount: BigNumber.from(prize.amount),
      tierIndex: prize.tier,
      pick: BigNumber.from(prize.pick)
    };
  });

  var totalValue = sumBigNumbers(prizes.map(function (prize) {
    return prize.amount;
  }));
  var drawResults = utils.filterResultsByValue({
    drawId: drawId,
    totalValue: totalValue,
    prizes: prizes
  }, maxPicksPerUser);
  return drawResults;
};

/**
 * Currently the Prize API is only running for a select group of networks.
 */

var PRIZE_API_SUPPORTED_NETWORKS = /*#__PURE__*/Object.freeze([NETWORK.avalanche, NETWORK.mainnet, NETWORK.polygon, NETWORK.optimism, NETWORK['goerli'], NETWORK['optimism-goerli']]);
/**
 * PoolTogether Prize API.
 * Provides easy access to PoolTogether Prize APIs.
 */

var PrizeApi = /*#__PURE__*/function () {
  function PrizeApi() {}

  /**
   * Fetches a users DrawResults for the provided draw id
   * @param chainId the chain id the PrizeDistributor is deployed on
   * @param usersAddress the address of the user to fetch draw results for
   * @param prizeDistributorAddress the address of the PrizeDistributor to fetch prizes for
   * @param drawId the id of the draw to check
   * @param maxPicksPerUser the maximum number of picks per user
   */
  PrizeApi.getUsersDrawResultsByDraw =
  /*#__PURE__*/
  function () {
    var _getUsersDrawResultsByDraw = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(chainId, usersAddress, prizeDistributorAddress, drawId, maxPicksPerUser) {
      var drawResults;
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return this.getUsersDrawResultsByDraws(chainId, usersAddress, prizeDistributorAddress, [drawId], [maxPicksPerUser]);

            case 2:
              drawResults = _context.sent;
              return _context.abrupt("return", drawResults[drawId]);

            case 4:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function getUsersDrawResultsByDraw(_x, _x2, _x3, _x4, _x5) {
      return _getUsersDrawResultsByDraw.apply(this, arguments);
    }

    return getUsersDrawResultsByDraw;
  }()
  /**
   * Fetches a users DrawResults for the provided draw ids.
   * Checks the status of the Prize API, falls back to the CloudFlare worker if Prize API status is invalid.
   * @param chainId the chain id the PrizeDistributor is deployed on
   * @param usersAddress the address of the user to fetch draw results for
   * @param prizeDistributorAddress the address of the PrizeDistributor to fetch prizes for
   * @param drawIds a list of draw ids to check for prizes
   * @param maxPicksPerUserPerDraw the maximum number of picks per user for each drwa
   */
  ;

  PrizeApi.getUsersDrawResultsByDraws =
  /*#__PURE__*/
  function () {
    var _getUsersDrawResultsByDraws = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(chainId, usersAddress, prizeDistributorAddress, drawIds, maxPicksPerUserPerDraw) {
      var _this = this;

      var drawResults, drawResultsPromises;
      return runtime_1.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              drawResults = {};
              drawResultsPromises = drawIds.map( /*#__PURE__*/function () {
                var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(drawId, index) {
                  var drawResult, apiStatus, _drawResult, _drawResult2, error;

                  return runtime_1.wrap(function _callee2$(_context2) {
                    while (1) {
                      switch (_context2.prev = _context2.next) {
                        case 0:
                          _context2.prev = 0;

                          if (PRIZE_API_SUPPORTED_NETWORKS.includes(chainId)) {
                            _context2.next = 9;
                            break;
                          }

                          console.warn("Prize API only supports networks: " + PRIZE_API_SUPPORTED_NETWORKS.join(', ') + ".");
                          _context2.next = 5;
                          return _this.computeDrawResultsOnCloudFlareWorker(chainId, usersAddress, prizeDistributorAddress, drawId);

                        case 5:
                          drawResult = _context2.sent;
                          drawResults[drawId] = drawResult;
                          _context2.next = 23;
                          break;

                        case 9:
                          _context2.next = 11;
                          return _this.checkPrizeApiStatus(chainId, prizeDistributorAddress, drawId);

                        case 11:
                          apiStatus = _context2.sent;

                          if (!apiStatus) {
                            _context2.next = 19;
                            break;
                          }

                          _context2.next = 15;
                          return _this.getDrawResultsFromPrizeApi(chainId, usersAddress, prizeDistributorAddress, drawId, maxPicksPerUserPerDraw[index]);

                        case 15:
                          _drawResult = _context2.sent;
                          drawResults[drawId] = _drawResult;
                          _context2.next = 23;
                          break;

                        case 19:
                          _context2.next = 21;
                          return _this.computeDrawResultsOnCloudFlareWorker(chainId, usersAddress, prizeDistributorAddress, drawId);

                        case 21:
                          _drawResult2 = _context2.sent;
                          drawResults[drawId] = _drawResult2;

                        case 23:
                          _context2.next = 30;
                          break;

                        case 25:
                          _context2.prev = 25;
                          _context2.t0 = _context2["catch"](0);
                          error = _context2.t0;
                          console.error(error.message);
                          throw error;

                        case 30:
                        case "end":
                          return _context2.stop();
                      }
                    }
                  }, _callee2, null, [[0, 25]]);
                }));

                return function (_x11, _x12) {
                  return _ref.apply(this, arguments);
                };
              }());
              _context3.next = 4;
              return Promise.all(drawResultsPromises);

            case 4:
              return _context3.abrupt("return", drawResults);

            case 5:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));

    function getUsersDrawResultsByDraws(_x6, _x7, _x8, _x9, _x10) {
      return _getUsersDrawResultsByDraws.apply(this, arguments);
    }

    return getUsersDrawResultsByDraws;
  }()
  /**
   * Fetches precomputed prizes from the Prize API.
   * The Prize API only supports a limited set of networks, see https://dev.pooltogether.com/protocol/api/prize-api for more info.
   * @param chainId
   * @param usersAddress
   * @param prizeDistributorAddress
   * @param drawId
   * @param maxPicksPerUser
   * @returns
   */
  ;

  PrizeApi.getDrawResultsFromPrizeApi =
  /*#__PURE__*/
  function () {
    var _getDrawResultsFromPrizeApi = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(chainId, usersAddress, prizeDistributorAddress, drawId, maxPicksPerUser) {
      var url, response, prizesJson, prizes, drawResult;
      return runtime_1.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              if (PRIZE_API_SUPPORTED_NETWORKS.includes(chainId)) {
                _context4.next = 2;
                break;
              }

              throw new Error("PrizeApi | Prize API only supports networks: " + PRIZE_API_SUPPORTED_NETWORKS.join(', ') + ".");

            case 2:
              url = this.getDrawResultsUrl(chainId, prizeDistributorAddress, usersAddress, drawId);
              _context4.next = 5;
              return fetch(url);

            case 5:
              response = _context4.sent;

              if (!(response.status === 404)) {
                _context4.next = 8;
                break;
              }

              return _context4.abrupt("return", createEmptyDrawResult(drawId));

            case 8:
              _context4.next = 10;
              return response.json();

            case 10:
              prizesJson = _context4.sent;
              prizes = deserializeBigNumbers(prizesJson);
              drawResult = formatDrawResultsFromPrizes(drawId, prizes, maxPicksPerUser);
              return _context4.abrupt("return", drawResult);

            case 14:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4, this);
    }));

    function getDrawResultsFromPrizeApi(_x13, _x14, _x15, _x16, _x17) {
      return _getDrawResultsFromPrizeApi.apply(this, arguments);
    }

    return getDrawResultsFromPrizeApi;
  }()
  /**
   * Computes the users prizes in a CloudFlare worker.
   * @param chainId
   * @param usersAddress
   * @param prizeDistributorAddress
   * @param drawId
   */
  ;

  PrizeApi.computeDrawResultsOnCloudFlareWorker =
  /*#__PURE__*/
  function () {
    var _computeDrawResultsOnCloudFlareWorker = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5(chainId, usersAddress, prizeDistributorAddress, drawId) {
      var url, response, drawResultsJson, LEGACY_drawResult, drawResult;
      return runtime_1.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              url = this.getCloudFlareDrawResultsUrl(chainId, prizeDistributorAddress, usersAddress, drawId);
              _context5.next = 3;
              return fetch(url);

            case 3:
              response = _context5.sent;
              _context5.next = 6;
              return response.json();

            case 6:
              drawResultsJson = _context5.sent;
              LEGACY_drawResult = deserializeBigNumbers(drawResultsJson);
              drawResult = formatDrawResultsFromLegacyDrawResults(LEGACY_drawResult);
              return _context5.abrupt("return", drawResult);

            case 10:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5, this);
    }));

    function computeDrawResultsOnCloudFlareWorker(_x18, _x19, _x20, _x21) {
      return _computeDrawResultsOnCloudFlareWorker.apply(this, arguments);
    }

    return computeDrawResultsOnCloudFlareWorker;
  }()
  /**
   * Computes the users prizes locally.
   * NOTE: This is a heavy calculation and not recommended on users devices.
   * @param usersAddress
   * @param prizeDistributorAddress
   * @param drawId
   * @param provider
   */
  ;

  PrizeApi.computeDrawResults =
  /*#__PURE__*/
  function () {
    var _computeDrawResults = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee6(usersAddress, prizeDistributorAddress, drawId, provider) {
      var prizeDistributorContract, response, drawCalculatorTimelockAddress, drawCalculatorTimelockContract, drawCalculatorAddress, drawCalculatorContract, drawBufferAddress, prizeDistributionBufferAddress, normalizedBalance, drawBufferContract, prizeDistributionBufferContract, draw, prizeDistribution, drawResults;
      return runtime_1.wrap(function _callee6$(_context6) {
        while (1) {
          switch (_context6.prev = _context6.next) {
            case 0:
              // Get Draw Calculator Timelock address
              prizeDistributorContract = contract(prizeDistributorAddress, PartialPrizeDistributorAbi, prizeDistributorAddress); // @ts-ignore

              _context6.next = 3;
              return batch(provider, prizeDistributorContract.getDrawCalculator());

            case 3:
              response = _context6.sent;
              drawCalculatorTimelockAddress = response[prizeDistributorAddress].getDrawCalculator[0]; // Get Draw Calculator address
              // It's assumed that the PristDistributor has a DrawCalculatorTimelock set up, if it does not, this second getDrawCalculator call will fail. That means the contract is not a DrawCalculatorTimelock and just a DrawCalculator.

              drawCalculatorTimelockContract = contract(drawCalculatorTimelockAddress, PartialDrawCalculatorTimelockAbi, drawCalculatorTimelockAddress);
              _context6.prev = 6;
              _context6.next = 9;
              return batch(provider, drawCalculatorTimelockContract.getDrawCalculator());

            case 9:
              response = _context6.sent;
              drawCalculatorAddress = response[drawCalculatorTimelockAddress].getDrawCalculator[0];
              _context6.next = 17;
              break;

            case 13:
              _context6.prev = 13;
              _context6.t0 = _context6["catch"](6);
              console.warn("Prize Distributor at " + prizeDistributorAddress + " is not using a DrawCalculatorTimelock");
              drawCalculatorAddress = drawCalculatorTimelockAddress;

            case 17:
              // Get Draw Buffer & Prize Distribution Buffer addresses and the users normalized balance
              drawCalculatorContract = contract(drawCalculatorAddress, PartialDrawCalculatorAbi, drawCalculatorAddress);
              _context6.next = 20;
              return batch(provider, drawCalculatorContract // @ts-ignore
              .getDrawBuffer().getPrizeDistributionBuffer().getNormalizedBalancesForDrawIds(usersAddress, [drawId]));

            case 20:
              response = _context6.sent;
              drawBufferAddress = response[drawCalculatorAddress].getDrawBuffer[0];
              prizeDistributionBufferAddress = response[drawCalculatorAddress].getPrizeDistributionBuffer[0];
              normalizedBalance = response[drawCalculatorAddress].getNormalizedBalancesForDrawIds[0][0]; // If user had no balance, short circuit

              if (!normalizedBalance.isZero()) {
                _context6.next = 26;
                break;
              }

              return _context6.abrupt("return", createEmptyDrawResult(drawId));

            case 26:
              // Get the draw and prize distribution
              drawBufferContract = contract(drawBufferAddress, PartialDrawBufferAbi, drawBufferAddress);
              prizeDistributionBufferContract = contract(prizeDistributionBufferAddress, PartialPrizeDistributionBufferAbi, prizeDistributionBufferAddress);
              _context6.next = 30;
              return batch(provider, // @ts-ignore
              drawBufferContract.getDraw(drawId), // @ts-ignore
              prizeDistributionBufferContract.getPrizeDistribution(drawId));

            case 30:
              response = _context6.sent;
              draw = response[drawBufferAddress].getDraw[0];
              prizeDistribution = response[prizeDistributionBufferAddress].getPrizeDistribution[0];
              drawResults = computeUserWinningPicksForRandomNumber(draw.winningRandomNumber, prizeDistribution.bitRangeSize, prizeDistribution.matchCardinality, prizeDistribution.numberOfPicks, prizeDistribution.prize, prizeDistribution.tiers, usersAddress, normalizedBalance, draw.drawId);
              return _context6.abrupt("return", utils.filterResultsByValue(drawResults, prizeDistribution.maxPicksPerUser));

            case 35:
            case "end":
              return _context6.stop();
          }
        }
      }, _callee6, null, [[6, 13]]);
    }));

    function computeDrawResults(_x22, _x23, _x24, _x25) {
      return _computeDrawResults.apply(this, arguments);
    }

    return computeDrawResults;
  }()
  /**
   * Checks the status of a particular draw and returns true if the data is available for the requested draw.
   * @param chainId
   * @param prizeDistributorAddress
   * @param drawId
   * @returns
   */
  ;

  PrizeApi.checkPrizeApiStatus =
  /*#__PURE__*/
  function () {
    var _checkPrizeApiStatus = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee7(chainId, prizeDistributorAddress, drawId) {
      var response, requestStatus, drawResultsStatusJson;
      return runtime_1.wrap(function _callee7$(_context7) {
        while (1) {
          switch (_context7.prev = _context7.next) {
            case 0:
              _context7.next = 2;
              return fetch(this.getDrawResultsStatusUrl(chainId, prizeDistributorAddress, drawId));

            case 2:
              response = _context7.sent;
              requestStatus = response.status;

              if (!(requestStatus !== 200)) {
                _context7.next = 6;
                break;
              }

              throw new Error("PrizeApi | Draw " + drawId + " for Prize Distributor " + prizeDistributorAddress + " on " + chainId + " calculation status not found.");

            case 6:
              _context7.next = 8;
              return response.json();

            case 8:
              drawResultsStatusJson = _context7.sent;

              if (!(drawResultsStatusJson.cliStatus !== undefined)) {
                _context7.next = 11;
                break;
              }

              return _context7.abrupt("return", drawResultsStatusJson.cliStatus === 'ok');

            case 11:
              return _context7.abrupt("return", drawResultsStatusJson.status === PrizeApiStatus.success);

            case 12:
            case "end":
              return _context7.stop();
          }
        }
      }, _callee7, this);
    }));

    function checkPrizeApiStatus(_x26, _x27, _x28) {
      return _checkPrizeApiStatus.apply(this, arguments);
    }

    return checkPrizeApiStatus;
  }() // URLs

  /**
   * Returns the URL for pre-calculated prizes from the Prize API
   * TODO: Fix the casing functions once Kames fixes the bug
   * @param chainId
   * @param prizeDistributorAddress
   * @param usersAddress
   * @param drawId
   * @returns
   */
  ;

  PrizeApi.getDrawResultsUrl = function getDrawResultsUrl(chainId, prizeDistributorAddress, usersAddress, drawId) {
    return "https://api.pooltogether.com/prizes/" + chainId + "/" + prizeDistributorAddress.toLowerCase() + "/draw/" + drawId + "/" + usersAddress.toLowerCase() + ".json";
  }
  /**
   * Returns the URL for the status of the calculations for the draw requested from the Prize API
   * @param chainId
   * @param prizeDistributorAddress
   * @param usersAddress
   * @param drawId
   * @returns
   */
  ;

  PrizeApi.getDrawResultsStatusUrl = function getDrawResultsStatusUrl(chainId, prizeDistributorAddress, drawId) {
    return "https://api.pooltogether.com/prizes/" + chainId + "/" + prizeDistributorAddress.toLowerCase() + "/draw/" + drawId + "/status.json";
  }
  /**
   * Returns the URL for the status of the calculations for the draw requested from the Prize API
   * @param chainId
   * @param prizeDistributorAddress
   * @param usersAddress
   * @param drawId
   * @returns
   */
  ;

  PrizeApi.getAllPrizesUrl = function getAllPrizesUrl(chainId, prizeDistributorAddress, drawId) {
    return "https://api.pooltogether.com/prizes/" + chainId + "/" + prizeDistributorAddress.toLowerCase() + "/draw/" + drawId + "/prizes.json";
  }
  /**
   * Returns the URL that the prizes can be calculated at on CloudFlare
   * @param chainId
   * @param prizeDistributorAddress
   * @param usersAddress
   * @param drawId
   * @returns
   */
  ;

  PrizeApi.getCloudFlareDrawResultsUrl = function getCloudFlareDrawResultsUrl(chainId, prizeDistributorAddress, usersAddress, drawId) {
    return "https://tsunami-prizes-production.pooltogether-api.workers.dev/" + chainId + "/" + prizeDistributorAddress + "/prizes/" + usersAddress + "/" + drawId + "/";
  };

  return PrizeApi;
}(); // Partial ABIs with the minimal interfaces to fetch the required data for computing prizes

var PartialPrizeDistributorAbi = [{
  inputs: [],
  name: 'getDrawCalculator',
  outputs: [{
    internalType: 'contract IDrawCalculator',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}];
var PartialDrawCalculatorTimelockAbi = [{
  inputs: [],
  name: 'getDrawCalculator',
  outputs: [{
    internalType: 'contract IDrawCalculator',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}];
var PartialDrawCalculatorAbi = [{
  inputs: [{
    internalType: 'address',
    name: '_user',
    type: 'address'
  }, {
    internalType: 'uint32[]',
    name: '_drawIds',
    type: 'uint32[]'
  }],
  name: 'getNormalizedBalancesForDrawIds',
  outputs: [{
    internalType: 'uint256[]',
    name: '',
    type: 'uint256[]'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'getPrizeDistributionBuffer',
  outputs: [{
    internalType: 'contract IPrizeDistributionBuffer',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'getDrawBuffer',
  outputs: [{
    internalType: 'contract IDrawBuffer',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}];
var PartialDrawBufferAbi = [{
  inputs: [{
    internalType: 'uint32',
    name: 'drawId',
    type: 'uint32'
  }],
  name: 'getDraw',
  outputs: [{
    components: [{
      internalType: 'uint256',
      name: 'winningRandomNumber',
      type: 'uint256'
    }, {
      internalType: 'uint32',
      name: 'drawId',
      type: 'uint32'
    }, {
      internalType: 'uint64',
      name: 'timestamp',
      type: 'uint64'
    }, {
      internalType: 'uint64',
      name: 'beaconPeriodStartedAt',
      type: 'uint64'
    }, {
      internalType: 'uint32',
      name: 'beaconPeriodSeconds',
      type: 'uint32'
    }],
    internalType: 'struct IDrawBeacon.Draw',
    name: '',
    type: 'tuple'
  }],
  stateMutability: 'view',
  type: 'function'
}];
var PartialPrizeDistributionBufferAbi = [{
  inputs: [{
    internalType: 'uint32',
    name: '_drawId',
    type: 'uint32'
  }],
  name: 'getPrizeDistribution',
  outputs: [{
    components: [{
      internalType: 'uint8',
      name: 'bitRangeSize',
      type: 'uint8'
    }, {
      internalType: 'uint8',
      name: 'matchCardinality',
      type: 'uint8'
    }, {
      internalType: 'uint32',
      name: 'startTimestampOffset',
      type: 'uint32'
    }, {
      internalType: 'uint32',
      name: 'endTimestampOffset',
      type: 'uint32'
    }, {
      internalType: 'uint32',
      name: 'maxPicksPerUser',
      type: 'uint32'
    }, {
      internalType: 'uint32',
      name: 'expiryDuration',
      type: 'uint32'
    }, {
      internalType: 'uint104',
      name: 'numberOfPicks',
      type: 'uint104'
    }, {
      internalType: 'uint32[16]',
      name: 'tiers',
      type: 'uint32[16]'
    }, {
      internalType: 'uint256',
      name: 'prize',
      type: 'uint256'
    }],
    internalType: 'struct IPrizeDistributionBuffer.PrizeDistribution',
    name: '',
    type: 'tuple'
  }],
  stateMutability: 'view',
  type: 'function'
}];

/**
 * A Prize Distributor.
 * Provides access to the contracts for viewing expiration times on draws, timelock timers and checking/claiming prizes for a user. Can be instantiated with an ethers Signer or Provider. Use a Signer if you want to claim transactions for a user. If a provider is provided, only read methods are available.
 */

var PrizeDistributor = /*#__PURE__*/function () {
  /**
   * Create an instance of a PrizeDistributor by providing the metadata of the PrizeDistributor contract, an ethers Provider or Signer for the network the PrizeDistributor contract is deployed on and a list of contract metadata for the other contracts that make up the PrizeDistributor.
   * @param prizeDistributorMetadata
   * @param signerOrProvider
   * @param contractMetadataList
   */
  function PrizeDistributor(prizeDistributorMetadata, signerOrProvider, contractMetadataList) {
    // Get contract metadata & ethers contracts
    var prizeDistributorContract = new Contract(prizeDistributorMetadata.address, prizeDistributorMetadata.abi, signerOrProvider); // Set data

    this.signerOrProvider = signerOrProvider;
    this.contractMetadataList = contractMetadataList;
    this.chainId = prizeDistributorMetadata.chainId;
    this.address = prizeDistributorMetadata.address; // Set metadata

    this.prizeDistributorMetadata = prizeDistributorMetadata; // Set ethers contracts

    this.prizeDistributorContract = prizeDistributorContract; // Initialized later - requires a fetch

    this.drawCalculatorTimelockMetadata = undefined;
    this.drawCalculatorTimelockContract = undefined;
    this.drawCalculatorMetadata = undefined;
    this.drawCalculatorContract = undefined;
    this.drawBufferMetadata = undefined;
    this.drawBufferContract = undefined;
    this.prizeDistributionsBufferMetadata = undefined;
    this.prizeDistributionsBufferContract = undefined;
    this.prizeTierHistoryMetadata = undefined;
    this.prizeTierHistoryContract = undefined;
  }
  /**
   * Returns a unique id string for this PrizeDistributor.
   * @returns a unique id for the PrizeDistributor
   */


  var _proto = PrizeDistributor.prototype;

  _proto.id = function id() {
    return this.prizeDistributorMetadata.address + "-" + this.prizeDistributorMetadata.chainId;
  } //////////////////////////// Ethers write functions ////////////////////////////

  /**
   * Fetches a users prizes for the provided draw and submits a transaction to claim them to the Signer.
   * PrizeDistributor must be initialized with a Signer.
   * @param drawId the draw id to claim prizes for
   * @param maxPicksPerUser the maximum picks per user from the PrizeDistribution for the provided draw id
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.claimPrizesByDraw =
  /*#__PURE__*/
  function () {
    var _claimPrizesByDraw = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(drawId, maxPicksPerUser, overrides) {
      var errorPrefix, usersAddress, drawResults;
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              errorPrefix = 'PrizeDistributors [claim] | ';
              _context.next = 3;
              return this.getUsersAddress(errorPrefix);

            case 3:
              usersAddress = _context.sent;
              _context.next = 6;
              return this.getUsersDrawResultsForDrawId(usersAddress, drawId, maxPicksPerUser);

            case 6:
              drawResults = _context.sent;
              return _context.abrupt("return", this.claimPrizesByDrawResults(drawResults, overrides));

            case 8:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function claimPrizesByDraw(_x, _x2, _x3) {
      return _claimPrizesByDraw.apply(this, arguments);
    }

    return claimPrizesByDraw;
  }()
  /**
   * Submits a transaction to claim a users prizes
   * PrizeDistributor must be initialized with a Signer.
   * @param drawResults the prize results for a user for a specific draw
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.claimPrizesByDrawResults =
  /*#__PURE__*/
  function () {
    var _claimPrizesByDrawResults = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(drawResults, overrides) {
      var errorPrefix, usersAddress, claim;
      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              errorPrefix = 'PrizeDistributors [claimPrizes] | ';
              _context2.next = 3;
              return this.getUsersAddress(errorPrefix);

            case 3:
              usersAddress = _context2.sent;
              _context2.next = 6;
              return this.validateSignerNetwork(errorPrefix);

            case 6:
              if (!drawResults.totalValue.isZero()) {
                _context2.next = 8;
                break;
              }

              throw new Error(errorPrefix + 'No prizes to claim.');

            case 8:
              claim = encodeWinningPicks(usersAddress, [drawResults]);

              if (!Boolean(overrides)) {
                _context2.next = 13;
                break;
              }

              return _context2.abrupt("return", this.prizeDistributorContract.claim(claim.userAddress, claim.drawIds, claim.encodedWinningPickIndices, overrides));

            case 13:
              return _context2.abrupt("return", this.prizeDistributorContract.claim(claim.userAddress, claim.drawIds, claim.encodedWinningPickIndices));

            case 14:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, this);
    }));

    function claimPrizesByDrawResults(_x4, _x5) {
      return _claimPrizesByDrawResults.apply(this, arguments);
    }

    return claimPrizesByDrawResults;
  }()
  /**
   * Submits a transaction to claim a users prizes across multiple draws
   * PrizeDistributor must be initialized with a Signer.
   * @param drawResults an object of the users draw results to claim keyed by draw ids
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.claimPrizesAcrossMultipleDrawsByDrawResults =
  /*#__PURE__*/
  function () {
    var _claimPrizesAcrossMultipleDrawsByDrawResults = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(drawResults, overrides) {
      var errorPrefix, usersAddress, drawResultsList, totalValueToClaim, claim;
      return runtime_1.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              errorPrefix = 'PrizeDistributors [claimPrizes] | ';
              _context3.next = 3;
              return this.getUsersAddress(errorPrefix);

            case 3:
              usersAddress = _context3.sent;
              _context3.next = 6;
              return this.validateSignerNetwork(errorPrefix);

            case 6:
              drawResultsList = Object.values(drawResults);
              totalValueToClaim = drawResultsList.reduce(function (total, drawResult) {
                return total.add(drawResult.totalValue);
              }, ethers.BigNumber.from(0));

              if (!totalValueToClaim.isZero()) {
                _context3.next = 10;
                break;
              }

              throw new Error(errorPrefix + 'No prizes to claim.');

            case 10:
              claim = encodeWinningPicks(usersAddress, drawResultsList);

              if (!Boolean(overrides)) {
                _context3.next = 15;
                break;
              }

              return _context3.abrupt("return", this.prizeDistributorContract.claim(claim.userAddress, claim.drawIds, claim.encodedWinningPickIndices, overrides));

            case 15:
              return _context3.abrupt("return", this.prizeDistributorContract.claim(claim.userAddress, claim.drawIds, claim.encodedWinningPickIndices));

            case 16:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3, this);
    }));

    function claimPrizesAcrossMultipleDrawsByDrawResults(_x6, _x7) {
      return _claimPrizesAcrossMultipleDrawsByDrawResults.apply(this, arguments);
    }

    return claimPrizesAcrossMultipleDrawsByDrawResults;
  }() //////////////////////////// Ethers read functions ////////////////////////////

  /**
   * Fetches the upcoming prize tier data from the prize tier history contract. This data is used for the next prize distribution that will be added to the Prize Distribution Buffer for the beacon Prize Pool.
   * @returns the upcoming prize tier
   */
  ;

  _proto.getUpcomingPrizeTier =
  /*#__PURE__*/
  function () {
    var _getUpcomingPrizeTier = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4() {
      var prizeTierHistoryContract, drawId, newestDrawIdResult, result, prizeTier;
      return runtime_1.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              _context4.next = 2;
              return this.getPrizeTierHistoryContract();

            case 2:
              prizeTierHistoryContract = _context4.sent;
              drawId = 0;
              _context4.prev = 4;
              _context4.next = 7;
              return prizeTierHistoryContract.functions.getNewestDrawId();

            case 7:
              newestDrawIdResult = _context4.sent;
              drawId = newestDrawIdResult[0];
              _context4.next = 14;
              break;

            case 11:
              _context4.prev = 11;
              _context4.t0 = _context4["catch"](4);
              console.log(_context4.t0);

            case 14:
              _context4.next = 16;
              return prizeTierHistoryContract.functions.getPrizeTier(drawId);

            case 16:
              result = _context4.sent;
              prizeTier = {
                bitRangeSize: result[0].bitRangeSize,
                expiryDuration: result[0].expiryDuration,
                maxPicksPerUser: result[0].maxPicksPerUser,
                prize: result[0].prize,
                tiers: result[0].tiers,
                endTimestampOffset: result[0].endTimestampOffset,
                drawId: result[0].drawId,
                dpr: result[0].dpr
              };
              return _context4.abrupt("return", prizeTier);

            case 19:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4, this, [[4, 11]]);
    }));

    function getUpcomingPrizeTier() {
      return _getUpcomingPrizeTier.apply(this, arguments);
    }

    return getUpcomingPrizeTier;
  }()
  /**
   * Fetches decimals, name and symbol for the Token that will be distributed.
   * @returns the decimals, name and symbol for the token
   */
  ;

  _proto.getTokenData =
  /*#__PURE__*/
  function () {
    var _getTokenData2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5() {
      var tokenContract;
      return runtime_1.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              _context5.next = 2;
              return this.getTokenContract();

            case 2:
              tokenContract = _context5.sent;
              return _context5.abrupt("return", getTokenData(tokenContract));

            case 4:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5, this);
    }));

    function getTokenData$1() {
      return _getTokenData2.apply(this, arguments);
    }

    return getTokenData$1;
  }()
  /**
   * Fetches the newest Draw in the DrawBuffer related to the PrizeDistributor.
   * NOTE: Will throw an error if the buffer is empty.
   * @returns the newest draw in the draw buffer
   */
  ;

  _proto.getNewestDraw =
  /*#__PURE__*/
  function () {
    var _getNewestDraw = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee6() {
      var drawBufferContract, result, draw;
      return runtime_1.wrap(function _callee6$(_context6) {
        while (1) {
          switch (_context6.prev = _context6.next) {
            case 0:
              _context6.next = 2;
              return this.getDrawBufferContract();

            case 2:
              drawBufferContract = _context6.sent;
              _context6.next = 5;
              return drawBufferContract.functions.getNewestDraw();

            case 5:
              result = _context6.sent;
              draw = result[0];
              return _context6.abrupt("return", {
                drawId: draw.drawId,
                timestamp: draw.timestamp,
                winningRandomNumber: draw.winningRandomNumber,
                beaconPeriodStartedAt: draw.beaconPeriodStartedAt,
                beaconPeriodSeconds: draw.beaconPeriodSeconds
              });

            case 8:
            case "end":
              return _context6.stop();
          }
        }
      }, _callee6, this);
    }));

    function getNewestDraw() {
      return _getNewestDraw.apply(this, arguments);
    }

    return getNewestDraw;
  }()
  /**
   * Fetches the oldest Draw in the DrawBuffer related to the PrizeDistributor.
   * @returns the oldest draw in the draw buffer
   */
  ;

  _proto.getOldestDraw =
  /*#__PURE__*/
  function () {
    var _getOldestDraw = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee7() {
      var drawBufferContract, result, draw;
      return runtime_1.wrap(function _callee7$(_context7) {
        while (1) {
          switch (_context7.prev = _context7.next) {
            case 0:
              _context7.next = 2;
              return this.getDrawBufferContract();

            case 2:
              drawBufferContract = _context7.sent;
              _context7.next = 5;
              return drawBufferContract.functions.getOldestDraw();

            case 5:
              result = _context7.sent;
              draw = result[0];
              return _context7.abrupt("return", {
                drawId: draw.drawId,
                timestamp: draw.timestamp,
                winningRandomNumber: draw.winningRandomNumber,
                beaconPeriodStartedAt: draw.beaconPeriodStartedAt,
                beaconPeriodSeconds: draw.beaconPeriodSeconds
              });

            case 8:
            case "end":
              return _context7.stop();
          }
        }
      }, _callee7, this);
    }));

    function getOldestDraw() {
      return _getOldestDraw.apply(this, arguments);
    }

    return getOldestDraw;
  }()
  /**
   * Fetches the newest PrizeDistribution in the PrizeDistributionBuffer related to the PrizeDistributor.
   * NOTE: Will throw an error if the buffer is empty.
   * @returns the newest prize distribution in the prize distribution buffer
   */
  ;

  _proto.getNewestPrizeDistribution =
  /*#__PURE__*/
  function () {
    var _getNewestPrizeDistribution = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee8() {
      var prizeDistributionBufferContract, result, prizeDistribution, drawId;
      return runtime_1.wrap(function _callee8$(_context8) {
        while (1) {
          switch (_context8.prev = _context8.next) {
            case 0:
              _context8.next = 2;
              return this.getPrizeDistributionsBufferContract();

            case 2:
              prizeDistributionBufferContract = _context8.sent;
              _context8.next = 5;
              return prizeDistributionBufferContract.functions.getNewestPrizeDistribution();

            case 5:
              result = _context8.sent;
              prizeDistribution = result[0];
              drawId = result[1];
              return _context8.abrupt("return", {
                prizeDistribution: {
                  matchCardinality: prizeDistribution.matchCardinality,
                  numberOfPicks: prizeDistribution.numberOfPicks,
                  tiers: prizeDistribution.tiers,
                  bitRangeSize: prizeDistribution.bitRangeSize,
                  prize: prizeDistribution.prize,
                  startTimestampOffset: prizeDistribution.drawStartTimestampOffset,
                  endTimestampOffset: prizeDistribution.drawEndTimestampOffset,
                  maxPicksPerUser: prizeDistribution.maxPicksPerUser
                },
                drawId: drawId
              });

            case 9:
            case "end":
              return _context8.stop();
          }
        }
      }, _callee8, this);
    }));

    function getNewestPrizeDistribution() {
      return _getNewestPrizeDistribution.apply(this, arguments);
    }

    return getNewestPrizeDistribution;
  }()
  /**
   * Fetches the oldest PrizeDistribution in the PrizeDistributionBuffer related to the PrizeDistributor.
   * @returns the oldest prize distribution in the prize distribution buffer
   */
  ;

  _proto.getOldestPrizeDistribution =
  /*#__PURE__*/
  function () {
    var _getOldestPrizeDistribution = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee9() {
      var prizeDistributionBufferContract, result, prizeDistribution, drawId;
      return runtime_1.wrap(function _callee9$(_context9) {
        while (1) {
          switch (_context9.prev = _context9.next) {
            case 0:
              _context9.next = 2;
              return this.getPrizeDistributionsBufferContract();

            case 2:
              prizeDistributionBufferContract = _context9.sent;
              _context9.next = 5;
              return prizeDistributionBufferContract.functions.getOldestPrizeDistribution();

            case 5:
              result = _context9.sent;
              prizeDistribution = result[0];
              drawId = result[1];
              return _context9.abrupt("return", {
                prizeDistribution: {
                  matchCardinality: prizeDistribution.matchCardinality,
                  numberOfPicks: prizeDistribution.numberOfPicks,
                  tiers: prizeDistribution.tiers,
                  bitRangeSize: prizeDistribution.bitRangeSize,
                  prize: prizeDistribution.prize,
                  startTimestampOffset: prizeDistribution.drawStartTimestampOffset,
                  endTimestampOffset: prizeDistribution.drawEndTimestampOffset,
                  maxPicksPerUser: prizeDistribution.maxPicksPerUser
                },
                drawId: drawId
              });

            case 9:
            case "end":
              return _context9.stop();
          }
        }
      }, _callee9, this);
    }));

    function getOldestPrizeDistribution() {
      return _getOldestPrizeDistribution.apply(this, arguments);
    }

    return getOldestPrizeDistribution;
  }()
  /**
   * Fetches the id and end time stamp of the draw that is currently in the DrawCalcluatorTimelock.
   * @returns the draw id and the end time as a unix time stamp in seconds
   */
  ;

  _proto.getTimelockDrawId =
  /*#__PURE__*/
  function () {
    var _getTimelockDrawId = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee10() {
      var drawCalculatorTimelockContract, timelockResult, _timelockResult$, endTimeSeconds, drawId;

      return runtime_1.wrap(function _callee10$(_context10) {
        while (1) {
          switch (_context10.prev = _context10.next) {
            case 0:
              _context10.next = 2;
              return this.getDrawCalculatorTimelockContract();

            case 2:
              drawCalculatorTimelockContract = _context10.sent;

              if (drawCalculatorTimelockContract) {
                _context10.next = 5;
                break;
              }

              return _context10.abrupt("return", null);

            case 5:
              _context10.next = 7;
              return drawCalculatorTimelockContract.functions.getTimelock();

            case 7:
              timelockResult = _context10.sent;
              _timelockResult$ = timelockResult[0], endTimeSeconds = _timelockResult$[0], drawId = _timelockResult$[1];
              return _context10.abrupt("return", {
                drawId: drawId,
                endTimeSeconds: endTimeSeconds
              });

            case 10:
            case "end":
              return _context10.stop();
          }
        }
      }, _callee10, this);
    }));

    function getTimelockDrawId() {
      return _getTimelockDrawId.apply(this, arguments);
    }

    return getTimelockDrawId;
  }()
  /**
   * Fetches the range of draw ids that are available in the DrawBuffer.
   * @returns a list of draw ids in the buffer
   */
  ;

  _proto.getDrawIdsFromDrawBuffer =
  /*#__PURE__*/
  function () {
    var _getDrawIdsFromDrawBuffer = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee11() {
      var _yield$Promise$allSet, oldestDrawResponse, newestDrawResponse, oldestId, newestId, drawIds, i;

      return runtime_1.wrap(function _callee11$(_context11) {
        while (1) {
          switch (_context11.prev = _context11.next) {
            case 0:
              _context11.next = 2;
              return Promise.allSettled([this.getOldestDraw(), this.getNewestDraw()]);

            case 2:
              _yield$Promise$allSet = _context11.sent;
              oldestDrawResponse = _yield$Promise$allSet[0];
              newestDrawResponse = _yield$Promise$allSet[1];

              if (!(newestDrawResponse.status === 'rejected' || oldestDrawResponse.status === 'rejected')) {
                _context11.next = 7;
                break;
              }

              return _context11.abrupt("return", []);

            case 7:
              oldestId = oldestDrawResponse.value.drawId;
              newestId = newestDrawResponse.value.drawId;
              drawIds = [];

              for (i = oldestId; i <= newestId; i++) {
                drawIds.push(i);
              }

              return _context11.abrupt("return", drawIds);

            case 12:
            case "end":
              return _context11.stop();
          }
        }
      }, _callee11, this);
    }));

    function getDrawIdsFromDrawBuffer() {
      return _getDrawIdsFromDrawBuffer.apply(this, arguments);
    }

    return getDrawIdsFromDrawBuffer;
  }()
  /**
   * Fetches the range of draw ids for the prize distributions that are available in the PrizeDistributionBuffer.
   * @returns a list of draw ids in the buffer
   */
  ;

  _proto.getDrawIdsFromPrizeDistributionBuffer =
  /*#__PURE__*/
  function () {
    var _getDrawIdsFromPrizeDistributionBuffer = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee12() {
      var _yield$Promise$allSet2, oldestPrizeDistributionResponse, newestPrizeDistributionResponse, oldestId, newestId, drawIds, i;

      return runtime_1.wrap(function _callee12$(_context12) {
        while (1) {
          switch (_context12.prev = _context12.next) {
            case 0:
              _context12.next = 2;
              return Promise.allSettled([this.getOldestPrizeDistribution(), this.getNewestPrizeDistribution()]);

            case 2:
              _yield$Promise$allSet2 = _context12.sent;
              oldestPrizeDistributionResponse = _yield$Promise$allSet2[0];
              newestPrizeDistributionResponse = _yield$Promise$allSet2[1];

              if (!(newestPrizeDistributionResponse.status === 'rejected' || oldestPrizeDistributionResponse.status === 'rejected')) {
                _context12.next = 7;
                break;
              }

              return _context12.abrupt("return", []);

            case 7:
              oldestId = oldestPrizeDistributionResponse.value.drawId;
              newestId = newestPrizeDistributionResponse.value.drawId;
              drawIds = [];

              for (i = oldestId; i <= newestId; i++) {
                drawIds.push(i);
              }

              return _context12.abrupt("return", drawIds);

            case 12:
            case "end":
              return _context12.stop();
          }
        }
      }, _callee12, this);
    }));

    function getDrawIdsFromPrizeDistributionBuffer() {
      return _getDrawIdsFromPrizeDistributionBuffer.apply(this, arguments);
    }

    return getDrawIdsFromPrizeDistributionBuffer;
  }()
  /**
   * Gets the list of draw ids of draws that have are available in both the DrawBuffer and PrizeDistributionBuffer.
   * @returns a list of draw ids in both buffers
   */
  ;

  _proto.getValidDrawIds =
  /*#__PURE__*/
  function () {
    var _getValidDrawIds = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee13() {
      var _yield$Promise$allSet3, oldestPrizeDistributionResponse, newestPrizeDistributionResponse, oldestDrawResponse, newestDrawResponse, oldestPrizeDistributionId, newestPrizeDistributionId, oldestDrawId, newestDrawId, newestIds, oldestId, newestId, validIds, i;

      return runtime_1.wrap(function _callee13$(_context13) {
        while (1) {
          switch (_context13.prev = _context13.next) {
            case 0:
              _context13.next = 2;
              return Promise.allSettled([this.getOldestPrizeDistribution(), this.getNewestPrizeDistribution(), this.getOldestDraw(), this.getNewestDraw()]);

            case 2:
              _yield$Promise$allSet3 = _context13.sent;
              oldestPrizeDistributionResponse = _yield$Promise$allSet3[0];
              newestPrizeDistributionResponse = _yield$Promise$allSet3[1];
              oldestDrawResponse = _yield$Promise$allSet3[2];
              newestDrawResponse = _yield$Promise$allSet3[3];

              if (!(oldestPrizeDistributionResponse.status === 'rejected' || newestPrizeDistributionResponse.status === 'rejected' || newestDrawResponse.status === 'rejected' || oldestDrawResponse.status === 'rejected')) {
                _context13.next = 9;
                break;
              }

              return _context13.abrupt("return", []);

            case 9:
              oldestPrizeDistributionId = oldestPrizeDistributionResponse.value.drawId;
              newestPrizeDistributionId = newestPrizeDistributionResponse.value.drawId;
              oldestDrawId = oldestDrawResponse.value.drawId;
              newestDrawId = newestDrawResponse.value.drawId;
              newestIds = [newestDrawId, newestPrizeDistributionId];
              oldestId = Math.max(oldestPrizeDistributionId, oldestDrawId);
              newestId = Math.min.apply(Math, newestIds);

              if (!(newestId < oldestId)) {
                _context13.next = 18;
                break;
              }

              return _context13.abrupt("return", []);

            case 18:
              validIds = [];

              for (i = oldestId; i <= newestId; i++) {
                validIds.push(i);
              }

              return _context13.abrupt("return", validIds);

            case 21:
            case "end":
              return _context13.stop();
          }
        }
      }, _callee13, this);
    }));

    function getValidDrawIds() {
      return _getValidDrawIds.apply(this, arguments);
    }

    return getValidDrawIds;
  }()
  /**
   * Fetches Draws and PrizeDistributions from their respective buffers for the provided list of draw ids.
   * @param drawIds the list of draw ids to fetch Draws and PrizeDistributions for
   * @returns an object full of Draws and PrizeDistributions keyed by their draw id
   */
  ;

  _proto.getDrawsAndPrizeDistributions =
  /*#__PURE__*/
  function () {
    var _getDrawsAndPrizeDistributions = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee14(drawIds) {
      var _yield$Promise$allSet4, drawsResponse, prizeDistributionsResponse, drawsAndPrizeDistributions;

      return runtime_1.wrap(function _callee14$(_context14) {
        while (1) {
          switch (_context14.prev = _context14.next) {
            case 0:
              _context14.next = 2;
              return Promise.allSettled([this.getDraws(drawIds), this.getPrizeDistributions(drawIds)]);

            case 2:
              _yield$Promise$allSet4 = _context14.sent;
              drawsResponse = _yield$Promise$allSet4[0];
              prizeDistributionsResponse = _yield$Promise$allSet4[1];

              if (!(drawsResponse.status === 'rejected' || prizeDistributionsResponse.status === 'rejected')) {
                _context14.next = 7;
                break;
              }

              return _context14.abrupt("return", this.getDrawsAndPrizeDistributions(drawIds.sort(function (a, b) {
                return a - b;
              }).slice(0, drawIds.length - 1)));

            case 7:
              drawsAndPrizeDistributions = {};
              Object.values(drawsResponse.value).forEach(function (draw, index) {
                drawsAndPrizeDistributions[draw.drawId] = {
                  draw: draw,
                  prizeDistribution: prizeDistributionsResponse.value[index]
                };
              });
              return _context14.abrupt("return", drawsAndPrizeDistributions);

            case 10:
            case "end":
              return _context14.stop();
          }
        }
      }, _callee14, this);
    }));

    function getDrawsAndPrizeDistributions(_x8) {
      return _getDrawsAndPrizeDistributions.apply(this, arguments);
    }

    return getDrawsAndPrizeDistributions;
  }()
  /**
   * Fetches a Draw from the DrawBuffer.
   * @param drawId the draw id of the Draw to fetch
   * @returns the Draw
   */
  ;

  _proto.getDraw =
  /*#__PURE__*/
  function () {
    var _getDraw = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee15(drawId) {
      var drawBufferContract, response;
      return runtime_1.wrap(function _callee15$(_context15) {
        while (1) {
          switch (_context15.prev = _context15.next) {
            case 0:
              _context15.next = 2;
              return this.getDrawBufferContract();

            case 2:
              drawBufferContract = _context15.sent;
              _context15.next = 5;
              return drawBufferContract.functions.getDraw(drawId);

            case 5:
              response = _context15.sent;
              return _context15.abrupt("return", {
                drawId: response[0].drawId,
                timestamp: response[0].timestamp,
                winningRandomNumber: response[0].winningRandomNumber,
                beaconPeriodStartedAt: response[0].beaconPeriodStartedAt,
                beaconPeriodSeconds: response[0].beaconPeriodSeconds
              });

            case 7:
            case "end":
              return _context15.stop();
          }
        }
      }, _callee15, this);
    }));

    function getDraw(_x9) {
      return _getDraw.apply(this, arguments);
    }

    return getDraw;
  }()
  /**
   * Fetches multiple Draws from the DrawBuffer.
   * @param drawIds a list of draw ids to fetch
   * @returns an object with Draws keyed by their draw ids
   */
  ;

  _proto.getDraws =
  /*#__PURE__*/
  function () {
    var _getDraws = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee16(drawIds) {
      var draws, drawBufferContract, response;
      return runtime_1.wrap(function _callee16$(_context16) {
        while (1) {
          switch (_context16.prev = _context16.next) {
            case 0:
              draws = {};

              if (!(!drawIds || drawIds.length === 0)) {
                _context16.next = 3;
                break;
              }

              return _context16.abrupt("return", draws);

            case 3:
              _context16.next = 5;
              return this.getDrawBufferContract();

            case 5:
              drawBufferContract = _context16.sent;
              _context16.next = 8;
              return drawBufferContract.functions.getDraws(drawIds);

            case 8:
              response = _context16.sent;
              response[0].forEach(function (draw) {
                draws[draw.drawId] = {
                  drawId: draw.drawId,
                  timestamp: draw.timestamp,
                  winningRandomNumber: draw.winningRandomNumber,
                  beaconPeriodStartedAt: draw.beaconPeriodStartedAt,
                  beaconPeriodSeconds: draw.beaconPeriodSeconds
                };
              });
              return _context16.abrupt("return", draws);

            case 11:
            case "end":
              return _context16.stop();
          }
        }
      }, _callee16, this);
    }));

    function getDraws(_x10) {
      return _getDraws.apply(this, arguments);
    }

    return getDraws;
  }()
  /**
   * Fetches a PrizeDistribution from the PrizeDistributionBuffer.
   * @param drawId the draw id for the PrizeDistribution to fetch
   * @returns the PrizeDistribution
   */
  ;

  _proto.getPrizeDistribution =
  /*#__PURE__*/
  function () {
    var _getPrizeDistribution = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee17(drawId) {
      var prizeDistributionsBufferContract, result;
      return runtime_1.wrap(function _callee17$(_context17) {
        while (1) {
          switch (_context17.prev = _context17.next) {
            case 0:
              _context17.next = 2;
              return this.getPrizeDistributionsBufferContract();

            case 2:
              prizeDistributionsBufferContract = _context17.sent;
              _context17.next = 5;
              return prizeDistributionsBufferContract.functions.getPrizeDistribution(drawId);

            case 5:
              result = _context17.sent;
              return _context17.abrupt("return", {
                matchCardinality: result[0].matchCardinality,
                tiers: result[0].tiers,
                bitRangeSize: result[0].bitRangeSize,
                maxPicksPerUser: result[0].maxPicksPerUser,
                numberOfPicks: result[0].numberOfPicks,
                expiryDuration: result[0].expiryDuration,
                prize: result[0].prize,
                startTimestampOffset: result[0].startTimestampOffset,
                endTimestampOffset: result[0].endTimestampOffset
              });

            case 7:
            case "end":
              return _context17.stop();
          }
        }
      }, _callee17, this);
    }));

    function getPrizeDistribution(_x11) {
      return _getPrizeDistribution.apply(this, arguments);
    }

    return getPrizeDistribution;
  }()
  /**
   * Fetches multiple PrizeDistributions from the PrizeDistributionBuffer.
   * @param drawIds a list of draw ids to fetch PrizeDistributions for
   * @returns an object with PrizeDistributions keyed by draw ids
   */
  ;

  _proto.getPrizeDistributions =
  /*#__PURE__*/
  function () {
    var _getPrizeDistributions = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee18(drawIds) {
      var prizeDistributionsBufferContract, prizeDistributionsResults, prizeDistributions;
      return runtime_1.wrap(function _callee18$(_context18) {
        while (1) {
          switch (_context18.prev = _context18.next) {
            case 0:
              if (!(!drawIds || drawIds.length === 0)) {
                _context18.next = 2;
                break;
              }

              return _context18.abrupt("return", {});

            case 2:
              _context18.next = 4;
              return this.getPrizeDistributionsBufferContract();

            case 4:
              prizeDistributionsBufferContract = _context18.sent;
              _context18.next = 7;
              return prizeDistributionsBufferContract.functions.getPrizeDistributions(drawIds);

            case 7:
              prizeDistributionsResults = _context18.sent;
              prizeDistributions = {};
              prizeDistributionsResults[0].forEach(function (result, index) {
                prizeDistributions[drawIds[index]] = {
                  matchCardinality: result.matchCardinality,
                  tiers: result.tiers,
                  bitRangeSize: result.bitRangeSize,
                  maxPicksPerUser: result.maxPicksPerUser,
                  numberOfPicks: result.numberOfPicks,
                  prize: result.prize,
                  expiryDuration: result.expiryDuration,
                  startTimestampOffset: result.startTimestampOffset,
                  endTimestampOffset: result.endTimestampOffset
                };
              });
              return _context18.abrupt("return", prizeDistributions);

            case 11:
            case "end":
              return _context18.stop();
          }
        }
      }, _callee18, this);
    }));

    function getPrizeDistributions(_x12) {
      return _getPrizeDistributions.apply(this, arguments);
    }

    return getPrizeDistributions;
  }()
  /**
   * Fetches the amount of tokens a user claimed for a draw.
   * @param usersAddress the address of the user to check
   * @param drawId the draw id to check
   * @returns the amount a user claimed
   */
  ;

  _proto.getUsersClaimedAmount =
  /*#__PURE__*/
  function () {
    var _getUsersClaimedAmount = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee19(usersAddress, drawId) {
      var errorPrefix, result;
      return runtime_1.wrap(function _callee19$(_context19) {
        while (1) {
          switch (_context19.prev = _context19.next) {
            case 0:
              errorPrefix = 'PrizeDistributors [getUsersClaimedAmount] |';
              _context19.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context19.next = 5;
              return this.prizeDistributorContract.functions.getDrawPayoutBalanceOf(usersAddress, drawId);

            case 5:
              result = _context19.sent;
              return _context19.abrupt("return", result[0]);

            case 7:
            case "end":
              return _context19.stop();
          }
        }
      }, _callee19, this);
    }));

    function getUsersClaimedAmount(_x13, _x14) {
      return _getUsersClaimedAmount.apply(this, arguments);
    }

    return getUsersClaimedAmount;
  }()
  /**
   * Fetches the amount of tokens a user claimed for multiple draws.
   * @param usersAddress the address of the user to check
   * @param drawIds a list of draw ids to check
   * @returns an object of claimed amounts keyed by the draw ids
   */
  ;

  _proto.getUsersClaimedAmounts =
  /*#__PURE__*/
  function () {
    var _getUsersClaimedAmounts = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee20(usersAddress, drawIds) {
      var _this = this;

      var claimedAmounts;
      return runtime_1.wrap(function _callee20$(_context20) {
        while (1) {
          switch (_context20.prev = _context20.next) {
            case 0:
              claimedAmounts = {};
              _context20.next = 3;
              return Promise.all(drawIds.map(function (drawId) {
                return _this.getUsersClaimedAmount(usersAddress, drawId).then(function (claimedAmount) {
                  claimedAmounts[drawId] = claimedAmount;
                });
              }));

            case 3:
              return _context20.abrupt("return", claimedAmounts);

            case 4:
            case "end":
              return _context20.stop();
          }
        }
      }, _callee20);
    }));

    function getUsersClaimedAmounts(_x15, _x16) {
      return _getUsersClaimedAmounts.apply(this, arguments);
    }

    return getUsersClaimedAmounts;
  }()
  /**
   * Fetches a users normalized balance for several draw ids.
   * @param usersAddress the address of a user to fetch normalized balances for
   * @param drawIds a list of draw ids to fetch normalized balances for
   * @returns an object of normalized balances keyed by draw ids
   */
  ;

  _proto.getUsersNormalizedBalancesForDrawIds =
  /*#__PURE__*/
  function () {
    var _getUsersNormalizedBalancesForDrawIds = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee21(usersAddress, drawIds) {
      var errorPrefix, drawCalculatorContract, result;
      return runtime_1.wrap(function _callee21$(_context21) {
        while (1) {
          switch (_context21.prev = _context21.next) {
            case 0:
              errorPrefix = 'PrizeDistributors [getUsersNormalizedBalancesForDrawIds] |';
              _context21.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context21.next = 5;
              return this.getDrawCalculatorContract();

            case 5:
              drawCalculatorContract = _context21.sent;
              _context21.next = 8;
              return drawCalculatorContract.functions.getNormalizedBalancesForDrawIds(usersAddress, drawIds);

            case 8:
              result = _context21.sent;
              return _context21.abrupt("return", result[0]);

            case 10:
            case "end":
              return _context21.stop();
          }
        }
      }, _callee21, this);
    }));

    function getUsersNormalizedBalancesForDrawIds(_x17, _x18) {
      return _getUsersNormalizedBalancesForDrawIds.apply(this, arguments);
    }

    return getUsersNormalizedBalancesForDrawIds;
  }()
  /**
   * Fetches the claimable prizes a user won for a specific Draw.
   * @param usersAddress the users address to fetch prizes for
   * @param drawId the draw id to fetch prizes for
   * @param maxPicksPerUser the maximum number of picks per user from the matching prize distribution
   * @returns the results for user for the provided draw
   */
  ;

  _proto.getUsersDrawResultsForDrawId =
  /*#__PURE__*/
  function () {
    var _getUsersDrawResultsForDrawId = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee22(usersAddress, drawId, maxPicksPerUser) {
      return runtime_1.wrap(function _callee22$(_context22) {
        while (1) {
          switch (_context22.prev = _context22.next) {
            case 0:
              return _context22.abrupt("return", PrizeApi.getUsersDrawResultsByDraw(this.chainId, usersAddress, this.prizeDistributorMetadata.address, drawId, maxPicksPerUser));

            case 1:
            case "end":
              return _context22.stop();
          }
        }
      }, _callee22, this);
    }));

    function getUsersDrawResultsForDrawId(_x19, _x20, _x21) {
      return _getUsersDrawResultsForDrawId.apply(this, arguments);
    }

    return getUsersDrawResultsForDrawId;
  }()
  /**
   * Fetches the claimable prizes a user won for multiple Draws.
   * @param usersAddress the users address to fetch prizes for
   * @param drawIds the draw ids to fetch prizes for
   * @param maxPicksPerUserPerDraw the maximum number of picks per user from the matching prize distribution for each draw
   * @returns the results for user for the provided draw
   */
  ;

  _proto.getUsersDrawResultsForDrawIds =
  /*#__PURE__*/
  function () {
    var _getUsersDrawResultsForDrawIds = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee23(usersAddress, drawIds, maxPicksPerUserPerDraw) {
      return runtime_1.wrap(function _callee23$(_context23) {
        while (1) {
          switch (_context23.prev = _context23.next) {
            case 0:
              return _context23.abrupt("return", PrizeApi.getUsersDrawResultsByDraws(this.chainId, usersAddress, this.prizeDistributorMetadata.address, drawIds, maxPicksPerUserPerDraw));

            case 1:
            case "end":
              return _context23.stop();
          }
        }
      }, _callee23, this);
    }));

    function getUsersDrawResultsForDrawIds(_x22, _x23, _x24) {
      return _getUsersDrawResultsForDrawIds.apply(this, arguments);
    }

    return getUsersDrawResultsForDrawIds;
  }() // NOTE: Claimed event functions commented out as events on networks other than Ethereum mainnet are unreliable.

  /**
   *
   * @param usersAddress
   * @returns
   */
  // async getUsersClaimedEvents(usersAddress: string) {
  //   const errorPrefix = 'PrizeDistributors [getUsersClaimedEvents] |'
  //   await validateAddress(errorPrefix, usersAddress)
  //   const eventFilter = this.prizeDistributorContract.filters.ClaimedDraw(usersAddress)
  //   return await this.prizeDistributorContract.queryFilter(eventFilter)
  // }

  /**
   *
   * @param usersAddress
   * @param draw
   * @returns
   */
  // async getUsersClaimedEvent(usersAddress: string, draw: Draw): Promise<Event> {
  //   const eventFilter = this.prizeDistributorContract.filters.ClaimedDraw(usersAddress, draw.drawId)
  //   const events = await this.prizeDistributorContract.queryFilter(eventFilter)
  //   return events[0]
  // }
  //////////////////////////// Ethers Contracts Initializers ////////////////////////////

  /**
   * Fetches a contract address, finds the relevant metadata in the ContractList and creates an ethers Contract for that contract. The ethers Contract is cached on the instance of the PrizeDistributor and is returned immediately if already stored.
   * @param key the key for the requested contract to be stored on the PrizeDistributor
   * @param contractType the contract name
   * @param getContractAddress a function to fetch the contract address
   * @returns an ethers Contract for the provided address and contract type
   */
  ;

  _proto.getAndSetEthersContract =
  /*#__PURE__*/
  function () {
    var _getAndSetEthersContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee24(key, contractType, getContractAddress) {
      var contractKey, metadataKey, contractAddress, _getMetadataAndContra, contractMetadata, contract;

      return runtime_1.wrap(function _callee24$(_context24) {
        while (1) {
          switch (_context24.prev = _context24.next) {
            case 0:
              contractKey = key + "Contract";
              metadataKey = key + "Metadata"; // @ts-ignore

              if (!(this[contractKey] !== undefined)) {
                _context24.next = 4;
                break;
              }

              return _context24.abrupt("return", this[contractKey]);

            case 4:
              _context24.next = 6;
              return getContractAddress();

            case 6:
              contractAddress = _context24.sent;
              _getMetadataAndContra = getMetadataAndContract(this.chainId, this.signerOrProvider, contractType, this.contractMetadataList, contractAddress), contractMetadata = _getMetadataAndContra.contractMetadata, contract = _getMetadataAndContra.contract; // @ts-ignore

              this[metadataKey] = contractMetadata; // @ts-ignore

              this[contractKey] = contract;
              return _context24.abrupt("return", contract);

            case 11:
            case "end":
              return _context24.stop();
          }
        }
      }, _callee24, this);
    }));

    function getAndSetEthersContract(_x25, _x26, _x27) {
      return _getAndSetEthersContract.apply(this, arguments);
    }

    return getAndSetEthersContract;
  }()
  /**
   * Fetches the address of the DrawCalculator and caches the ethers Contract for the DrawCalculator
   * @returns an ethers Contract for the DrawCalculator related to this PrizeDistributor
   */
  ;

  _proto.getDrawCalculatorContract =
  /*#__PURE__*/
  function () {
    var _getDrawCalculatorContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee26() {
      var _this2 = this;

      var getAddress;
      return runtime_1.wrap(function _callee26$(_context26) {
        while (1) {
          switch (_context26.prev = _context26.next) {
            case 0:
              getAddress = /*#__PURE__*/function () {
                var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee25() {
                  var result, address, contractMetadata, drawCalculatorTimelockContract;
                  return runtime_1.wrap(function _callee25$(_context25) {
                    while (1) {
                      switch (_context25.prev = _context25.next) {
                        case 0:
                          _context25.next = 2;
                          return _this2.prizeDistributorContract.functions.getDrawCalculator();

                        case 2:
                          result = _context25.sent;
                          address = result[0];
                          contractMetadata = _this2.contractMetadataList.find(function (contractMetadata) {
                            return contractMetadata.chainId === _this2.chainId && contractMetadata.address === address;
                          });

                          if (!((contractMetadata == null ? void 0 : contractMetadata.type) === ContractType.DrawCalculatorTimelock)) {
                            _context25.next = 14;
                            break;
                          }

                          drawCalculatorTimelockContract = new Contract(address, contractMetadata.abi, _this2.signerOrProvider);
                          _context25.next = 9;
                          return drawCalculatorTimelockContract.functions.getDrawCalculator();

                        case 9:
                          result = _context25.sent;
                          address = result[0];
                          return _context25.abrupt("return", address);

                        case 14:
                          return _context25.abrupt("return", address);

                        case 15:
                        case "end":
                          return _context25.stop();
                      }
                    }
                  }, _callee25);
                }));

                return function getAddress() {
                  return _ref.apply(this, arguments);
                };
              }();

              return _context26.abrupt("return", this.getAndSetEthersContract('drawCalculator', ContractType.DrawCalculator, getAddress));

            case 2:
            case "end":
              return _context26.stop();
          }
        }
      }, _callee26, this);
    }));

    function getDrawCalculatorContract() {
      return _getDrawCalculatorContract.apply(this, arguments);
    }

    return getDrawCalculatorContract;
  }()
  /**
   * Fetches the address of the DrawBuffer and caches the ethers Contract for the DrawBuffer.
   * @returns an ethers Contract for the DrawBuffer related to this PrizeDistributor
   */
  ;

  _proto.getDrawBufferContract =
  /*#__PURE__*/
  function () {
    var _getDrawBufferContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee28() {
      var _this3 = this;

      var getAddress;
      return runtime_1.wrap(function _callee28$(_context28) {
        while (1) {
          switch (_context28.prev = _context28.next) {
            case 0:
              getAddress = /*#__PURE__*/function () {
                var _ref2 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee27() {
                  var drawCalculatorContract, result;
                  return runtime_1.wrap(function _callee27$(_context27) {
                    while (1) {
                      switch (_context27.prev = _context27.next) {
                        case 0:
                          _context27.next = 2;
                          return _this3.getDrawCalculatorContract();

                        case 2:
                          drawCalculatorContract = _context27.sent;
                          _context27.next = 5;
                          return drawCalculatorContract.functions.getDrawBuffer();

                        case 5:
                          result = _context27.sent;
                          return _context27.abrupt("return", result[0]);

                        case 7:
                        case "end":
                          return _context27.stop();
                      }
                    }
                  }, _callee27);
                }));

                return function getAddress() {
                  return _ref2.apply(this, arguments);
                };
              }();

              return _context28.abrupt("return", this.getAndSetEthersContract('drawBuffer', ContractType.DrawBuffer, getAddress));

            case 2:
            case "end":
              return _context28.stop();
          }
        }
      }, _callee28, this);
    }));

    function getDrawBufferContract() {
      return _getDrawBufferContract.apply(this, arguments);
    }

    return getDrawBufferContract;
  }()
  /**
   * Fetches the address of the PrizeDistributionsBuffer and caches the ethers Contract for the PrizeDistributionsBuffer.
   * @returns an ethers Contract for the PrizeDistributionsBuffer related to this PrizeDistributor
   */
  ;

  _proto.getPrizeDistributionsBufferContract =
  /*#__PURE__*/
  function () {
    var _getPrizeDistributionsBufferContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee30() {
      var _this4 = this;

      var getAddress;
      return runtime_1.wrap(function _callee30$(_context30) {
        while (1) {
          switch (_context30.prev = _context30.next) {
            case 0:
              getAddress = /*#__PURE__*/function () {
                var _ref3 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee29() {
                  var drawCalculatorContract, result;
                  return runtime_1.wrap(function _callee29$(_context29) {
                    while (1) {
                      switch (_context29.prev = _context29.next) {
                        case 0:
                          _context29.next = 2;
                          return _this4.getDrawCalculatorContract();

                        case 2:
                          drawCalculatorContract = _context29.sent;
                          _context29.next = 5;
                          return drawCalculatorContract.functions.getPrizeDistributionBuffer();

                        case 5:
                          result = _context29.sent;
                          return _context29.abrupt("return", result[0]);

                        case 7:
                        case "end":
                          return _context29.stop();
                      }
                    }
                  }, _callee29);
                }));

                return function getAddress() {
                  return _ref3.apply(this, arguments);
                };
              }();

              return _context30.abrupt("return", this.getAndSetEthersContract('prizeDistributionsBuffer', ContractType.PrizeDistributionBuffer, getAddress));

            case 2:
            case "end":
              return _context30.stop();
          }
        }
      }, _callee30, this);
    }));

    function getPrizeDistributionsBufferContract() {
      return _getPrizeDistributionsBufferContract.apply(this, arguments);
    }

    return getPrizeDistributionsBufferContract;
  }()
  /**
   * Fetches the address of the Token that is distributed by this PrizeDistributor and caches the ethers Contract for the ERC20 Token.
   * @returns an ethers Contract for the ERC20 Token related to this PrizeDistributor
   */
  ;

  _proto.getTokenContract =
  /*#__PURE__*/
  function () {
    var _getTokenContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee32() {
      var _this5 = this;

      var getAddress, tokenAddress, tokenMetadata, tokenContract;
      return runtime_1.wrap(function _callee32$(_context32) {
        while (1) {
          switch (_context32.prev = _context32.next) {
            case 0:
              if (!(this.tokenContract !== undefined)) {
                _context32.next = 2;
                break;
              }

              return _context32.abrupt("return", this.tokenContract);

            case 2:
              getAddress = /*#__PURE__*/function () {
                var _ref4 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee31() {
                  var result;
                  return runtime_1.wrap(function _callee31$(_context31) {
                    while (1) {
                      switch (_context31.prev = _context31.next) {
                        case 0:
                          _context31.next = 2;
                          return _this5.prizeDistributorContract.functions.getToken();

                        case 2:
                          result = _context31.sent;
                          return _context31.abrupt("return", result[0]);

                        case 4:
                        case "end":
                          return _context31.stop();
                      }
                    }
                  }, _callee31);
                }));

                return function getAddress() {
                  return _ref4.apply(this, arguments);
                };
              }();

              _context32.next = 5;
              return getAddress();

            case 5:
              tokenAddress = _context32.sent;
              tokenMetadata = createContractMetadata(this.chainId, tokenAddress, ContractType.Token, ERC20Abi);
              tokenContract = new Contract(tokenMetadata.address, tokenMetadata.abi, this.signerOrProvider);
              this.tokenMetadata = tokenMetadata;
              this.tokenContract = tokenContract;
              return _context32.abrupt("return", tokenContract);

            case 11:
            case "end":
              return _context32.stop();
          }
        }
      }, _callee32, this);
    }));

    function getTokenContract() {
      return _getTokenContract.apply(this, arguments);
    }

    return getTokenContract;
  }();

  _proto.getPrizeDistributionFactoryContract = /*#__PURE__*/function () {
    var _getPrizeDistributionFactoryContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee34() {
      var _this6 = this;

      var getAddress;
      return runtime_1.wrap(function _callee34$(_context34) {
        while (1) {
          switch (_context34.prev = _context34.next) {
            case 0:
              getAddress = /*#__PURE__*/function () {
                var _ref5 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee33() {
                  var prizeDistributionBufferContract, result;
                  return runtime_1.wrap(function _callee33$(_context33) {
                    while (1) {
                      switch (_context33.prev = _context33.next) {
                        case 0:
                          _context33.next = 2;
                          return _this6.getPrizeDistributionsBufferContract();

                        case 2:
                          prizeDistributionBufferContract = _context33.sent;
                          _context33.next = 5;
                          return prizeDistributionBufferContract.functions.manager();

                        case 5:
                          result = _context33.sent;
                          return _context33.abrupt("return", result[0]);

                        case 7:
                        case "end":
                          return _context33.stop();
                      }
                    }
                  }, _callee33);
                }));

                return function getAddress() {
                  return _ref5.apply(this, arguments);
                };
              }();

              return _context34.abrupt("return", this.getAndSetEthersContract('prizeDistributionFactory', ContractType.PrizeDistributionFactory, getAddress));

            case 2:
            case "end":
              return _context34.stop();
          }
        }
      }, _callee34, this);
    }));

    function getPrizeDistributionFactoryContract() {
      return _getPrizeDistributionFactoryContract.apply(this, arguments);
    }

    return getPrizeDistributionFactoryContract;
  }();

  _proto.getPrizeTierHistoryContract = /*#__PURE__*/function () {
    var _getPrizeTierHistoryContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee36() {
      var _this7 = this;

      var getAddress;
      return runtime_1.wrap(function _callee36$(_context36) {
        while (1) {
          switch (_context36.prev = _context36.next) {
            case 0:
              getAddress = /*#__PURE__*/function () {
                var _ref6 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee35() {
                  var prizeDistributionFactoryContract, result;
                  return runtime_1.wrap(function _callee35$(_context35) {
                    while (1) {
                      switch (_context35.prev = _context35.next) {
                        case 0:
                          _context35.next = 2;
                          return _this7.getPrizeDistributionFactoryContract();

                        case 2:
                          prizeDistributionFactoryContract = _context35.sent;
                          _context35.next = 5;
                          return prizeDistributionFactoryContract.functions.prizeTierHistory();

                        case 5:
                          result = _context35.sent;
                          return _context35.abrupt("return", result[0]);

                        case 7:
                        case "end":
                          return _context35.stop();
                      }
                    }
                  }, _callee35);
                }));

                return function getAddress() {
                  return _ref6.apply(this, arguments);
                };
              }();

              return _context36.abrupt("return", this.getAndSetEthersContract('prizeTierHistory', ContractType.PrizeTierHistory, getAddress));

            case 2:
            case "end":
              return _context36.stop();
          }
        }
      }, _callee36, this);
    }));

    function getPrizeTierHistoryContract() {
      return _getPrizeTierHistoryContract.apply(this, arguments);
    }

    return getPrizeTierHistoryContract;
  }()
  /*
   * Fetches the address of the set DrawCalculator on the PrizeDistributor and checks if it is a DrawCalculatorTimelock
   * @returns an ethers Contract for the DrawCalculator related to this PrizeDistributor
   */
  ;

  _proto.getDrawCalculatorTimelockContract =
  /*#__PURE__*/
  function () {
    var _getDrawCalculatorTimelockContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee38() {
      var _this8 = this;

      var getAddress, contractAddress, _getMetadataAndContra2, contractMetadata, contract;

      return runtime_1.wrap(function _callee38$(_context38) {
        while (1) {
          switch (_context38.prev = _context38.next) {
            case 0:
              if (!this.drawCalculatorTimelockContract) {
                _context38.next = 2;
                break;
              }

              return _context38.abrupt("return", this.drawCalculatorTimelockContract);

            case 2:
              getAddress = /*#__PURE__*/function () {
                var _ref7 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee37() {
                  var result, address, contractMetadata;
                  return runtime_1.wrap(function _callee37$(_context37) {
                    while (1) {
                      switch (_context37.prev = _context37.next) {
                        case 0:
                          _context37.next = 2;
                          return _this8.prizeDistributorContract.functions.getDrawCalculator();

                        case 2:
                          result = _context37.sent;
                          address = result[0];
                          contractMetadata = _this8.contractMetadataList.find(function (contractMetadata) {
                            return contractMetadata.chainId === _this8.chainId && contractMetadata.address === address && contractMetadata.type === ContractType.DrawCalculatorTimelock;
                          });
                          return _context37.abrupt("return", contractMetadata == null ? void 0 : contractMetadata.address);

                        case 6:
                        case "end":
                          return _context37.stop();
                      }
                    }
                  }, _callee37);
                }));

                return function getAddress() {
                  return _ref7.apply(this, arguments);
                };
              }();

              _context38.next = 5;
              return getAddress();

            case 5:
              contractAddress = _context38.sent;

              if (contractAddress) {
                _context38.next = 8;
                break;
              }

              return _context38.abrupt("return", null);

            case 8:
              _getMetadataAndContra2 = getMetadataAndContract(this.chainId, this.signerOrProvider, ContractType.DrawCalculatorTimelock, this.contractMetadataList, contractAddress), contractMetadata = _getMetadataAndContra2.contractMetadata, contract = _getMetadataAndContra2.contract;
              this.drawCalculatorTimelockMetadata = contractMetadata;
              this.drawCalculatorTimelockContract = contract;
              return _context38.abrupt("return", contract);

            case 12:
            case "end":
              return _context38.stop();
          }
        }
      }, _callee38, this);
    }));

    function getDrawCalculatorTimelockContract() {
      return _getDrawCalculatorTimelockContract.apply(this, arguments);
    }

    return getDrawCalculatorTimelockContract;
  }() //////////////////////////// Methods ////////////////////////////

  /**
   * Returns the users address of the provided Signer.
   * PrizeDistributor must be initialized with a Signer.
   * @param errorPrefix the class and function name of where the error occurred
   * @returns the address of the user
   */
  ;

  _proto.getUsersAddress =
  /*#__PURE__*/
  function () {
    var _getUsersAddress = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee39(errorPrefix) {
      return runtime_1.wrap(function _callee39$(_context39) {
        while (1) {
          switch (_context39.prev = _context39.next) {
            case 0:
              if (errorPrefix === void 0) {
                errorPrefix = 'PrizeDistributors [getUsersAddress] |';
              }

              _context39.next = 3;
              return this.validateIsSigner(errorPrefix);

            case 3:
              _context39.next = 5;
              return this.signerOrProvider.getAddress();

            case 5:
              return _context39.abrupt("return", _context39.sent);

            case 6:
            case "end":
              return _context39.stop();
          }
        }
      }, _callee39, this);
    }));

    function getUsersAddress(_x28) {
      return _getUsersAddress.apply(this, arguments);
    }

    return getUsersAddress;
  }() //////////////////////////// Validation methods ////////////////////////////

  /**
   * Validates that a Signer is on the network the PrizeDistributor is deployed on.
   * @param errorPrefix the class and function name of where the error occurred
   */
  ;

  _proto.validateSignerNetwork =
  /*#__PURE__*/
  function () {
    var _validateSignerNetwork2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee40(errorPrefix) {
      return runtime_1.wrap(function _callee40$(_context40) {
        while (1) {
          switch (_context40.prev = _context40.next) {
            case 0:
              validateSignerNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 1:
            case "end":
              return _context40.stop();
          }
        }
      }, _callee40, this);
    }));

    function validateSignerNetwork$1(_x29) {
      return _validateSignerNetwork2.apply(this, arguments);
    }

    return validateSignerNetwork$1;
  }()
  /**
   * Validates that the data provided for providerOrSigner is a Signer.
   * @param errorPrefix the class and function name of where the error occurred
   */
  ;

  _proto.validateIsSigner =
  /*#__PURE__*/
  function () {
    var _validateIsSigner2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee41(errorPrefix) {
      return runtime_1.wrap(function _callee41$(_context41) {
        while (1) {
          switch (_context41.prev = _context41.next) {
            case 0:
              validateIsSigner(errorPrefix, this.signerOrProvider);

            case 1:
            case "end":
              return _context41.stop();
          }
        }
      }, _callee41, this);
    }));

    function validateIsSigner$1(_x30) {
      return _validateIsSigner2.apply(this, arguments);
    }

    return validateIsSigner$1;
  }();

  return PrizeDistributor;
}();
/**
 * Utility function to create several PrizeDistributors from a contract list.
 * @param contractList a list of all of the relevant contract metadata for all of the PrizeDistributors to create
 * @param signersOrProviders signers or providers for all of the networks the PrizeDistributors are deployed on keyed by the chain id
 * @returns a list of PrizeDistributors
 */

function initializePrizeDistributors(contractList, signersOrProviders) {
  var prizeDistributorContracts = contractList.contracts.filter(function (contract) {
    return contract.type === ContractType.PrizeDistributor;
  });
  return prizeDistributorContracts.map(function (prizeDistributorContract) {
    return new PrizeDistributor(prizeDistributorContract, signersOrProviders[prizeDistributorContract.chainId], contractList.contracts);
  });
}

var ERC20AbiWithPermit = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'Approval',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'Transfer',
  type: 'event'
}, {
  inputs: [],
  name: 'DOMAIN_SEPARATOR',
  outputs: [{
    internalType: 'bytes32',
    name: '',
    type: 'bytes32'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }],
  name: 'allowance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'approve',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'decimals',
  outputs: [{
    internalType: 'uint8',
    name: '',
    type: 'uint8'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'subtractedValue',
    type: 'uint256'
  }],
  name: 'decreaseAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'addedValue',
    type: 'uint256'
  }],
  name: 'increaseAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'name',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }],
  name: 'nonces',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'deadline',
    type: 'uint256'
  }, {
    internalType: 'uint8',
    name: 'v',
    type: 'uint8'
  }, {
    internalType: 'bytes32',
    name: 'r',
    type: 'bytes32'
  }, {
    internalType: 'bytes32',
    name: 's',
    type: 'bytes32'
  }],
  name: 'permit',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'symbol',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'totalSupply',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'transfer',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'transferFrom',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}];

/**
 * A Prize Pool.
 * Provides read only functions for the contracts that make up the deployment of this Prize Pool.
 */

var PrizePool = /*#__PURE__*/function () {
  /**
   * Create an instance of a PrizePool by providing the metadata for the YieldSourcePrizePool contract, an ethers Provider or Signer for the network the Prize Pool is deployed on and a list of contract metadata for the other contracts that make up the Prize Pool.
   * @constructor
   * @param prizePoolMetadata the metadata for the YieldSourcePrizePool contract in the Prize Pool
   * @param signerOrProvider a Provider or Signer for the network the Prize Pool deployment is on
   * @param contractMetadataList an array of metadata for the Prize Pool
   */
  function PrizePool(prizePoolMetadata, signerOrProvider, contractMetadataList) {
    var _this = this;

    // Set basic data
    this.contractMetadataList = contractMetadataList;
    this.signerOrProvider = signerOrProvider;
    this.chainId = prizePoolMetadata.chainId;
    this.address = prizePoolMetadata.address; // Get prizePool ethers contract

    var prizePoolContract = new Contract(this.address, prizePoolMetadata.abi, this.signerOrProvider); // Get eip2612PermitAndDeposit metadata & ethers contracts

    var eip2612PermitAndDepositContractMetadata = this.contractMetadataList.find(function (contract) {
      return contract.chainId === _this.chainId && contract.type === ContractType.EIP2612PermitAndDeposit;
    });
    var eip2612PermitAndDeposit = !!eip2612PermitAndDepositContractMetadata ? getMetadataAndContract(this.chainId, this.signerOrProvider, ContractType.EIP2612PermitAndDeposit, this.contractMetadataList) : undefined; // Set metadata

    this.prizePoolMetadata = prizePoolMetadata;
    this.eip2612PermitAndDepositMetadata = eip2612PermitAndDeposit == null ? void 0 : eip2612PermitAndDeposit.contractMetadata;
    this.ticketMetadata = undefined;
    this.tokenMetadata = undefined; // Set ethers contracts

    this.prizePoolContract = prizePoolContract;
    this.eip2612PermitAndDepositContract = eip2612PermitAndDeposit == null ? void 0 : eip2612PermitAndDeposit.contract;
    this.ticketContract = undefined;
    this.tokenContract = undefined;
  }
  /**
   * Returns a unique id string for this Prize Pool.
   * @returns a unique id for the Prize Pool
   */


  var _proto = PrizePool.prototype;

  _proto.id = function id() {
    return this.prizePoolMetadata.address + "-" + this.prizePoolMetadata.chainId;
  } //////////////////////////// Ethers read functions ////////////////////////////

  /**
   * Fetches a users balances for the Prize Pool underlying Token and Ticket.
   * @param usersAddress the users address to fetch balances for
   * @returns the users balances for the underlying deposit token and the ticket token
   */
  ;

  _proto.getUsersPrizePoolBalances =
  /*#__PURE__*/
  function () {
    var _getUsersPrizePoolBalances = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(usersAddress) {
      var errorPrefix, tokenContract, ticketContract, tokenBalancePromise, ticketBalancePromise, _yield$Promise$all, token, ticket;

      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              errorPrefix = 'PrizePool [getUsersPrizePoolBalances] | ';
              _context.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context.next = 5;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 5:
              _context.next = 7;
              return this.getTokenContract();

            case 7:
              tokenContract = _context.sent;
              _context.next = 10;
              return this.getTicketContract();

            case 10:
              ticketContract = _context.sent;
              tokenBalancePromise = getUsersERC20Balance(usersAddress, tokenContract);
              ticketBalancePromise = getUsersERC20Balance(usersAddress, ticketContract);
              _context.next = 15;
              return Promise.all([tokenBalancePromise, ticketBalancePromise]);

            case 15:
              _yield$Promise$all = _context.sent;
              token = _yield$Promise$all[0];
              ticket = _yield$Promise$all[1];
              return _context.abrupt("return", {
                token: token,
                ticket: ticket
              });

            case 19:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function getUsersPrizePoolBalances(_x) {
      return _getUsersPrizePoolBalances.apply(this, arguments);
    }

    return getUsersPrizePoolBalances;
  }()
  /**
   * Fetches a users balance for the Prize Pools Ticket.
   * @param usersAddress the address to fetch the balance for
   * @returns the users ticket balance
   */
  ;

  _proto.getUsersTicketBalance =
  /*#__PURE__*/
  function () {
    var _getUsersTicketBalance = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(usersAddress) {
      var errorPrefix, ticketContract;
      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              errorPrefix = 'PrizePool [getUsersTicketBalance] | ';
              _context2.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context2.next = 5;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 5:
              _context2.next = 7;
              return this.getTicketContract();

            case 7:
              ticketContract = _context2.sent;
              return _context2.abrupt("return", getUsersERC20Balance(usersAddress, ticketContract));

            case 9:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, this);
    }));

    function getUsersTicketBalance(_x2) {
      return _getUsersTicketBalance.apply(this, arguments);
    }

    return getUsersTicketBalance;
  }()
  /**
   * Fetches a users Ticket TWAB at a specific unix timestamp.
   * @param usersAddress the address to fetch the ticket TWAB for
   * @param startTimestamp the start timestamp to fetch in seconds
   * @param endTimestamp the end timestamp to fetch in seconds
   * @returns the users TWAB between the requested times
   */
  ;

  _proto.getUsersAverageBalanceBetween =
  /*#__PURE__*/
  function () {
    var _getUsersAverageBalanceBetween = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(usersAddress, startTimestamp, endTimestamp) {
      var errorPrefix, ticketContract, result, twab;
      return runtime_1.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              errorPrefix = 'PrizePool [getUsersAverageBalanceBetween] | ';
              _context3.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context3.next = 5;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 5:
              _context3.next = 7;
              return this.getTicketContract();

            case 7:
              ticketContract = _context3.sent;
              _context3.next = 10;
              return ticketContract.functions.getAverageBalanceBetween(usersAddress, startTimestamp, endTimestamp);

            case 10:
              result = _context3.sent;
              twab = result[0];
              return _context3.abrupt("return", twab);

            case 13:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3, this);
    }));

    function getUsersAverageBalanceBetween(_x3, _x4, _x5) {
      return _getUsersAverageBalanceBetween.apply(this, arguments);
    }

    return getUsersAverageBalanceBetween;
  }()
  /**
   * Fetches a users Ticket TWAB at a specific unix timestamp.
   * @param usersAddress the address to fetch the ticket TWAB for
   * @param unixTimestamp the unix timestamp to fetch in seconds
   * @returns the users TWAB at the requested time
   */
  ;

  _proto.getUsersTicketTwabAt =
  /*#__PURE__*/
  function () {
    var _getUsersTicketTwabAt = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(usersAddress, unixTimestamp) {
      var errorPrefix, ticketContract, result, twab;
      return runtime_1.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              errorPrefix = 'PrizePool [getUsersTicketBalance] | ';
              _context4.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context4.next = 5;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 5:
              _context4.next = 7;
              return this.getTicketContract();

            case 7:
              ticketContract = _context4.sent;
              _context4.next = 10;
              return ticketContract.functions.getBalanceAt(usersAddress, unixTimestamp);

            case 10:
              result = _context4.sent;
              twab = result[0];
              return _context4.abrupt("return", twab);

            case 13:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4, this);
    }));

    function getUsersTicketTwabAt(_x6, _x7) {
      return _getUsersTicketTwabAt.apply(this, arguments);
    }

    return getUsersTicketTwabAt;
  }()
  /**
   * Fetches a users balance for the Prize Pools underlying Token.
   * @param usersAddress the address to fetch the balance for
   * @returns the users token balance
   */
  ;

  _proto.getUsersTokenBalance =
  /*#__PURE__*/
  function () {
    var _getUsersTokenBalance = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5(usersAddress) {
      var errorPrefix, tokenContract;
      return runtime_1.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              errorPrefix = 'PrizePool [getUsersTokenBalance] | ';
              _context5.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context5.next = 5;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 5:
              _context5.next = 7;
              return this.getTokenContract();

            case 7:
              tokenContract = _context5.sent;
              return _context5.abrupt("return", getUsersERC20Balance(usersAddress, tokenContract));

            case 9:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5, this);
    }));

    function getUsersTokenBalance(_x8) {
      return _getUsersTokenBalance.apply(this, arguments);
    }

    return getUsersTokenBalance;
  }()
  /**
   * Fetches a users deposit allowance for the Prize Pool.
   * @param usersAddress the address to fetch the deposit allowance for
   * @returns the amount the user has approved for deposits
   */
  ;

  _proto.getUsersDepositAllowance =
  /*#__PURE__*/
  function () {
    var _getUsersDepositAllowance = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee6(usersAddress) {
      var errorPrefix, tokenContract, prizePoolAddress, result, allowanceUnformatted;
      return runtime_1.wrap(function _callee6$(_context6) {
        while (1) {
          switch (_context6.prev = _context6.next) {
            case 0:
              errorPrefix = 'PrizePool [getUsersDepositAllowance] | ';
              _context6.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context6.next = 5;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 5:
              _context6.next = 7;
              return this.getTokenContract();

            case 7:
              tokenContract = _context6.sent;
              prizePoolAddress = this.prizePoolMetadata.address;
              _context6.next = 11;
              return tokenContract.functions.allowance(usersAddress, prizePoolAddress);

            case 11:
              result = _context6.sent;
              allowanceUnformatted = result[0];
              return _context6.abrupt("return", {
                allowanceUnformatted: allowanceUnformatted,
                isApproved: !allowanceUnformatted.isZero()
              });

            case 14:
            case "end":
              return _context6.stop();
          }
        }
      }, _callee6, this);
    }));

    function getUsersDepositAllowance(_x9) {
      return _getUsersDepositAllowance.apply(this, arguments);
    }

    return getUsersDepositAllowance;
  }()
  /**
   * Fetches the address a user has delegated to.
   * @param usersAddress the address to fetch the delegate for
   * @returns the address a user has delegated to
   */
  ;

  _proto.getUsersTicketDelegate =
  /*#__PURE__*/
  function () {
    var _getUsersTicketDelegate = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee7(usersAddress) {
      var errorPrefix, ticketContract, result;
      return runtime_1.wrap(function _callee7$(_context7) {
        while (1) {
          switch (_context7.prev = _context7.next) {
            case 0:
              errorPrefix = 'PrizePool [getUsersTicketDelegate] | ';
              _context7.next = 3;
              return validateAddress(errorPrefix, usersAddress);

            case 3:
              _context7.next = 5;
              return this.getTicketContract();

            case 5:
              ticketContract = _context7.sent;
              _context7.next = 8;
              return ticketContract.functions.delegateOf(usersAddress);

            case 8:
              result = _context7.sent;
              return _context7.abrupt("return", result[0]);

            case 10:
            case "end":
              return _context7.stop();
          }
        }
      }, _callee7, this);
    }));

    function getUsersTicketDelegate(_x10) {
      return _getUsersTicketDelegate.apply(this, arguments);
    }

    return getUsersTicketDelegate;
  }()
  /**
   * Fetches decimals, name and symbol for the underling Token.
   * @returns decimals, name and symbol for the underling token
   */
  ;

  _proto.getTokenData =
  /*#__PURE__*/
  function () {
    var _getTokenData2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee8() {
      var errorPrefix, tokenContract;
      return runtime_1.wrap(function _callee8$(_context8) {
        while (1) {
          switch (_context8.prev = _context8.next) {
            case 0:
              errorPrefix = 'PrizePool [getTokenData] | ';
              _context8.next = 3;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 3:
              _context8.next = 5;
              return this.getTokenContract();

            case 5:
              tokenContract = _context8.sent;
              return _context8.abrupt("return", getTokenData(tokenContract));

            case 7:
            case "end":
              return _context8.stop();
          }
        }
      }, _callee8, this);
    }));

    function getTokenData$1() {
      return _getTokenData2.apply(this, arguments);
    }

    return getTokenData$1;
  }()
  /**
   * Fetches decimals, name and symbol for the Ticket.
   * @returns decimals, name and symbol for the ticket
   */
  ;

  _proto.getTicketData =
  /*#__PURE__*/
  function () {
    var _getTicketData = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee9() {
      var errorPrefix, ticketContract;
      return runtime_1.wrap(function _callee9$(_context9) {
        while (1) {
          switch (_context9.prev = _context9.next) {
            case 0:
              errorPrefix = 'PrizePool [getTicketData] | ';
              _context9.next = 3;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 3:
              _context9.next = 5;
              return this.getTicketContract();

            case 5:
              ticketContract = _context9.sent;
              return _context9.abrupt("return", getTokenData(ticketContract));

            case 7:
            case "end":
              return _context9.stop();
          }
        }
      }, _callee9, this);
    }));

    function getTicketData() {
      return _getTicketData.apply(this, arguments);
    }

    return getTicketData;
  }()
  /**
   * Fetches total supply for the Ticket.
   * @returns the total supply of the ticket
   */
  ;

  _proto.getTicketTotalSupply =
  /*#__PURE__*/
  function () {
    var _getTicketTotalSupply = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee10() {
      var errorPrefix, ticketContract, result, totalSupply;
      return runtime_1.wrap(function _callee10$(_context10) {
        while (1) {
          switch (_context10.prev = _context10.next) {
            case 0:
              errorPrefix = 'PrizePool [getTicketData] | ';
              _context10.next = 3;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 3:
              _context10.next = 5;
              return this.getTicketContract();

            case 5:
              ticketContract = _context10.sent;
              _context10.next = 8;
              return ticketContract.functions.totalSupply();

            case 8:
              result = _context10.sent;
              totalSupply = result[0];
              return _context10.abrupt("return", totalSupply);

            case 11:
            case "end":
              return _context10.stop();
          }
        }
      }, _callee10, this);
    }));

    function getTicketTotalSupply() {
      return _getTicketTotalSupply.apply(this, arguments);
    }

    return getTicketTotalSupply;
  }()
  /**
   * Fetches total supply for the Ticket accounting for the TWAB.
   * @param unixTimestamp the unix timestamp to fetch in seconds
   * @returns the ticket total supply TWAB at the requested time
   */
  ;

  _proto.getTicketTwabTotalSupplyAt =
  /*#__PURE__*/
  function () {
    var _getTicketTwabTotalSupplyAt = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee11(unixTimestamp) {
      var errorPrefix, ticketContract, result, totalSupply;
      return runtime_1.wrap(function _callee11$(_context11) {
        while (1) {
          switch (_context11.prev = _context11.next) {
            case 0:
              errorPrefix = 'PrizePool [getTicketTwabTotalSupplyAt] | ';
              _context11.next = 3;
              return validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId);

            case 3:
              _context11.next = 5;
              return this.getTicketContract();

            case 5:
              ticketContract = _context11.sent;
              _context11.next = 8;
              return ticketContract.functions.getTotalSupplyAt(unixTimestamp);

            case 8:
              result = _context11.sent;
              totalSupply = result[0];
              return _context11.abrupt("return", totalSupply);

            case 11:
            case "end":
              return _context11.stop();
          }
        }
      }, _callee11, this);
    }));

    function getTicketTwabTotalSupplyAt(_x11) {
      return _getTicketTwabTotalSupplyAt.apply(this, arguments);
    }

    return getTicketTwabTotalSupplyAt;
  }() // NOTE: Gas estimates are commented out as they are quite unreliable.

  /**
   * Fetches a gas estimate for depositing from the Prize Pool.
   * @param usersAddress string
   * @param amount BigNumber
   * @returns BigNumber
   */
  // async getDepositGasEstimate(usersAddress: string, amount: BigNumber): Promise<BigNumber> {
  //   const errorPrefix = 'PrizePool [getUsersDepositAllowance] | '
  //   await validateAddress(errorPrefix, usersAddress)
  //   await validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId)
  //   return await this.prizePoolContract.estimateGas.depositTo(usersAddress, amount)
  // }

  /**
   * Fetches a gas estimate for withdrawing from the Prize Pool.
   * @param usersAddress string
   * @param amount BigNumber
   * @returns BigNumber
   */
  // async getWithdrawGasEstimate(usersAddress: string, amount: BigNumber): Promise<BigNumber> {
  //   const errorPrefix = 'PrizePool [getWithdrawGasEstimate] | '
  //   await validateAddress(errorPrefix, usersAddress)
  //   await validateSignerOrProviderNetwork(errorPrefix, this.signerOrProvider, this.chainId)
  //   return await this.prizePoolContract.estimateGas.withdrawFrom(usersAddress, amount)
  // }

  /**
   * Fetches a gas estimate for approving deposits into the Prize Pool.
   * @param usersAddress string
   * @returns BigNumber
   */
  // async getApprovalGasEstimate(usersAddress: string): Promise<BigNumber> {
  //   const errorPrefix = 'PrizePool [getApprovalGasEstimate] | '
  //   await validateAddress(errorPrefix, usersAddress)
  //   const tokenContract = await this.getTokenContract()
  //   const prizePoolAddress = this.prizePoolMetadata.address
  //   return await tokenContract.estimateGas.approve(prizePoolAddress, MaxUint256)
  // }
  //////////////////////////// Ethers Contracts Initializers ////////////////////////////

  /**
   * Fetches the addresses to build an instance of an ethers Contract for the Ticket
   * @returns an ethers contract for the ticket
   */
  ;

  _proto.getTicketContract =
  /*#__PURE__*/
  function () {
    var _getTicketContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee13() {
      var _this2 = this;

      var getAddress, ticketAddress, _getMetadataAndContra, ticketMetadata, ticketContract;

      return runtime_1.wrap(function _callee13$(_context13) {
        while (1) {
          switch (_context13.prev = _context13.next) {
            case 0:
              if (!(this.ticketContract !== undefined)) {
                _context13.next = 2;
                break;
              }

              return _context13.abrupt("return", this.ticketContract);

            case 2:
              getAddress = /*#__PURE__*/function () {
                var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee12() {
                  var result;
                  return runtime_1.wrap(function _callee12$(_context12) {
                    while (1) {
                      switch (_context12.prev = _context12.next) {
                        case 0:
                          _context12.next = 2;
                          return _this2.prizePoolContract.functions.getTicket();

                        case 2:
                          result = _context12.sent;
                          return _context12.abrupt("return", result[0]);

                        case 4:
                        case "end":
                          return _context12.stop();
                      }
                    }
                  }, _callee12);
                }));

                return function getAddress() {
                  return _ref.apply(this, arguments);
                };
              }();

              _context13.next = 5;
              return getAddress();

            case 5:
              ticketAddress = _context13.sent;
              _getMetadataAndContra = getMetadataAndContract(this.chainId, this.signerOrProvider, ContractType.Ticket, this.contractMetadataList, ticketAddress), ticketMetadata = _getMetadataAndContra.contractMetadata, ticketContract = _getMetadataAndContra.contract;
              this.ticketMetadata = ticketMetadata;
              this.ticketContract = ticketContract;
              return _context13.abrupt("return", ticketContract);

            case 10:
            case "end":
              return _context13.stop();
          }
        }
      }, _callee13, this);
    }));

    function getTicketContract() {
      return _getTicketContract.apply(this, arguments);
    }

    return getTicketContract;
  }()
  /**
   * Fetches the addresses to build an instance of an ethers Contract for the underlying Token
   * @param options optional settings or toggles
   * @returns an ethers contract for the underlying token
   */
  ;

  _proto.getTokenContract =
  /*#__PURE__*/
  function () {
    var _getTokenContract = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee15(options) {
      var _this3 = this;

      var getAddress, tokenAddress, tokenMetadata, tokenContract;
      return runtime_1.wrap(function _callee15$(_context15) {
        while (1) {
          switch (_context15.prev = _context15.next) {
            case 0:
              if (options != null && options.eip2612) this.tokenContract = undefined;

              if (!(this.tokenContract !== undefined)) {
                _context15.next = 3;
                break;
              }

              return _context15.abrupt("return", this.tokenContract);

            case 3:
              getAddress = /*#__PURE__*/function () {
                var _ref2 = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee14() {
                  var result;
                  return runtime_1.wrap(function _callee14$(_context14) {
                    while (1) {
                      switch (_context14.prev = _context14.next) {
                        case 0:
                          _context14.next = 2;
                          return _this3.prizePoolContract.functions.getToken();

                        case 2:
                          result = _context14.sent;
                          return _context14.abrupt("return", result[0]);

                        case 4:
                        case "end":
                          return _context14.stop();
                      }
                    }
                  }, _callee14);
                }));

                return function getAddress() {
                  return _ref2.apply(this, arguments);
                };
              }();

              _context15.next = 6;
              return getAddress();

            case 6:
              tokenAddress = _context15.sent;
              tokenMetadata = createContractMetadata(this.chainId, tokenAddress, ContractType.Token, options != null && options.eip2612 ? ERC20AbiWithPermit : ERC20Abi);
              tokenContract = new Contract(tokenMetadata.address, tokenMetadata.abi, this.signerOrProvider);
              this.tokenMetadata = tokenMetadata;
              this.tokenContract = tokenContract;
              return _context15.abrupt("return", tokenContract);

            case 12:
            case "end":
              return _context15.stop();
          }
        }
      }, _callee15, this);
    }));

    function getTokenContract(_x12) {
      return _getTokenContract.apply(this, arguments);
    }

    return getTokenContract;
  }();

  return PrizePool;
}();
/**
 * A utility function to create several PrizePools from a contract list.
 * @param contractList a list of all of the relevant contract metadata for all of the Prize Pools
 * @param providers providers for all of the networks in the list of Prize Pools
 * @returns a list of initialized PrizePools
 */

function initializePrizePools(contractList, providers) {
  var prizePoolContractLists = sortContractsByContractTypeAndChildren(contractList.contracts, ContractType.YieldSourcePrizePool);
  var prizePools = [];
  prizePoolContractLists.forEach(function (contracts) {
    var prizePoolMetadata = contracts.find(function (contract) {
      return contract.type === ContractType.YieldSourcePrizePool;
    });
    var provider = providers[prizePoolMetadata.chainId];

    try {
      prizePools.push(new PrizePool(prizePoolMetadata, provider, contractList.contracts));
    } catch (e) {
      var error = e;
      console.error(error.message);
    }
  });
  return prizePools;
}

/**
 * A Prize Pool Network.
 * The network consists of one or more Prize Pools and Prize Distributors. PrizePoolNetwork provides read only functions for reading data from the contracts that make up the network. Initializes several PrizePools and PrizeDistributors on creation.
 */

var PrizePoolNetwork = /*#__PURE__*/function () {
  /**
   * Create an instance of a PrizePoolNetwork by providing ethers Providers for each relevant network and a Contract List.
   * @constructor
   * @param providers ethers Providers for each network in the Prize Pool Network, keyed by their chain id.
   * @param prizePoolNetworkContractList a Contract List containing all of the relevant metadata for the Prize Pool Network.
   */
  function PrizePoolNetwork(providers, prizePoolNetworkContractList) {
    this.providers = providers;
    this.contractList = prizePoolNetworkContractList;
    this.prizePools = initializePrizePools(prizePoolNetworkContractList, providers);
    this.prizeDistributors = initializePrizeDistributors(prizePoolNetworkContractList, providers); // DrawBeacon

    var drawBeaconContractMetadata = prizePoolNetworkContractList.contracts.find(function (c) {
      return c.type === ContractType.DrawBeacon;
    });
    var beaconChainId = drawBeaconContractMetadata.chainId;
    var beaconProvider = providers[beaconChainId];
    var drawBeaconContract = new Contract(drawBeaconContractMetadata.address, drawBeaconContractMetadata.abi, beaconProvider); // DrawBuffer

    var drawBufferContractMetadata = prizePoolNetworkContractList.contracts.find(function (c) {
      return c.type === ContractType.DrawBuffer && c.chainId === beaconChainId;
    });
    var drawBufferContract = new Contract(drawBufferContractMetadata.address, drawBufferContractMetadata.abi, beaconProvider); // Set values

    this.beaconChainId = beaconChainId;
    this.beaconAddress = drawBeaconContractMetadata.address;
    this.drawBeaconMetadata = drawBeaconContractMetadata;
    this.drawBeaconContract = drawBeaconContract;
    this.drawBufferMetadata = drawBufferContractMetadata;
    this.drawBufferContract = drawBufferContract;
  }
  /**
   * Returns a unique id string for this PrizePoolNetwork.
   * @returns a unique id for the PrizePoolNetwork
   */


  var _proto = PrizePoolNetwork.prototype;

  _proto.id = function id() {
    return "prize-pool-network-" + this.beaconChainId + "-" + this.beaconAddress;
  } //////////////////////////// Ethers read functions ////////////////////////////

  /**
   * Fetch the users balances for all relevant tokens for all Prize Pools in the Prize Pool Network.
   * @param usersAddress address to get balances for.
   * @returns an array of objects containing the chain id & Prize Pool address and a balances object with the users balances for relevant tokens to the prize pool
   */
  ;

  _proto.getUsersPrizePoolBalances =
  /*#__PURE__*/
  function () {
    var _getUsersPrizePoolBalances = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(usersAddress) {
      var balancesPromises;
      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              balancesPromises = this.prizePools.map( /*#__PURE__*/function () {
                var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(prizePool) {
                  var balances;
                  return runtime_1.wrap(function _callee$(_context) {
                    while (1) {
                      switch (_context.prev = _context.next) {
                        case 0:
                          _context.next = 2;
                          return prizePool.getUsersPrizePoolBalances(usersAddress);

                        case 2:
                          balances = _context.sent;
                          return _context.abrupt("return", {
                            chainId: prizePool.chainId,
                            address: prizePool.address,
                            balances: balances
                          });

                        case 4:
                        case "end":
                          return _context.stop();
                      }
                    }
                  }, _callee);
                }));

                return function (_x2) {
                  return _ref.apply(this, arguments);
                };
              }());
              return _context2.abrupt("return", Promise.all(balancesPromises));

            case 2:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, this);
    }));

    function getUsersPrizePoolBalances(_x) {
      return _getUsersPrizePoolBalances.apply(this, arguments);
    }

    return getUsersPrizePoolBalances;
  }()
  /**
   * Fetch the current Draw Beacon period data from the beacon Prize Pool.
   * @returns the current draw beacon period.
   */
  ;

  _proto.getDrawBeaconPeriod =
  /*#__PURE__*/
  function () {
    var _getDrawBeaconPeriod = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3() {
      var _yield$Promise$all, periodSecondsResult, periodStartedAtResult, nextDrawIdResult, startedAtSeconds, periodSeconds, endsAtSeconds, drawId;

      return runtime_1.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return Promise.all([this.drawBeaconContract.functions.getBeaconPeriodSeconds(), this.drawBeaconContract.functions.getBeaconPeriodStartedAt(), this.drawBeaconContract.functions.getNextDrawId()]);

            case 2:
              _yield$Promise$all = _context3.sent;
              periodSecondsResult = _yield$Promise$all[0];
              periodStartedAtResult = _yield$Promise$all[1];
              nextDrawIdResult = _yield$Promise$all[2];
              startedAtSeconds = periodStartedAtResult[0];
              periodSeconds = periodSecondsResult[0];
              endsAtSeconds = startedAtSeconds.add(periodSeconds);
              drawId = nextDrawIdResult[0];
              return _context3.abrupt("return", {
                startedAtSeconds: startedAtSeconds,
                periodSeconds: periodSeconds,
                endsAtSeconds: endsAtSeconds,
                drawId: drawId
              });

            case 11:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3, this);
    }));

    function getDrawBeaconPeriod() {
      return _getDrawBeaconPeriod.apply(this, arguments);
    }

    return getDrawBeaconPeriod;
  }()
  /**
   * Fetch the range of available draw ids in the Draw Buffer for the beacon Prize Pool.
   * @returns an array of draw ids
   */
  ;

  _proto.getBeaconChainDrawIds =
  /*#__PURE__*/
  function () {
    var _getBeaconChainDrawIds = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4() {
      var _yield$Promise$allSet, oldestDrawResponse, newestDrawResponse, oldestId, newestId, drawIds, i;

      return runtime_1.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              _context4.next = 2;
              return Promise.allSettled([this.drawBufferContract.functions.getOldestDraw(), this.drawBufferContract.functions.getNewestDraw()]);

            case 2:
              _yield$Promise$allSet = _context4.sent;
              oldestDrawResponse = _yield$Promise$allSet[0];
              newestDrawResponse = _yield$Promise$allSet[1];

              if (!(newestDrawResponse.status === 'rejected' || oldestDrawResponse.status === 'rejected')) {
                _context4.next = 7;
                break;
              }

              return _context4.abrupt("return", []);

            case 7:
              oldestId = oldestDrawResponse.value[0].drawId;
              newestId = newestDrawResponse.value[0].drawId;
              drawIds = [];

              for (i = oldestId; i <= newestId; i++) {
                drawIds.push(i);
              }

              return _context4.abrupt("return", drawIds);

            case 12:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4, this);
    }));

    function getBeaconChainDrawIds() {
      return _getBeaconChainDrawIds.apply(this, arguments);
    }

    return getBeaconChainDrawIds;
  }()
  /**
   * Fetch all of the available Draws in the Draw Buffer for the beacon Prize Pool.
   * @returns an object of draws keyed by their draw id
   */
  ;

  _proto.getBeaconChainDraws =
  /*#__PURE__*/
  function () {
    var _getBeaconChainDraws = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5() {
      var drawIds, draws, result;
      return runtime_1.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              _context5.next = 2;
              return this.getBeaconChainDrawIds();

            case 2:
              drawIds = _context5.sent;
              draws = {};
              _context5.next = 6;
              return this.drawBufferContract.functions.getDraws(drawIds);

            case 6:
              result = _context5.sent;
              result[0].forEach(function (draw) {
                draws[draw.drawId] = {
                  drawId: draw.drawId,
                  timestamp: draw.timestamp,
                  winningRandomNumber: draw.winningRandomNumber,
                  beaconPeriodStartedAt: draw.beaconPeriodStartedAt,
                  beaconPeriodSeconds: draw.beaconPeriodSeconds
                };
              });
              return _context5.abrupt("return", draws);

            case 9:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5, this);
    }));

    function getBeaconChainDraws() {
      return _getBeaconChainDraws.apply(this, arguments);
    }

    return getBeaconChainDraws;
  }()
  /**
   * Returns a PrizePool from the list of Prize Pools that was created on initialization by their primary key. The primary key of a Prize Pool is the chain id it is on and the address of the YieldSourcePrizePool contract.
   * @param chainId the chain id the requested prize pool is on
   * @param address the address of the YieldSourcePrizePool contract
   * @returns
   */
  ;

  _proto.getPrizePool = function getPrizePool(chainId, address) {
    return this.prizePools.find(function (prizePool) {
      return prizePool.chainId === chainId && prizePool.address === address;
    });
  }
  /**
   * Returns a PrizeDistributor from the list of Prize Distributors that was created on initialization by their primary key. The primary key of a Prize Disctributor is the chain id it is on and the address of the PrizeDistributor contract.
   * @param chainId the chain id the requested prize distributor is on
   * @param address the address of the PrizeDistributor contract
   * @returns
   */
  ;

  _proto.getPrizeDistributor = function getPrizeDistributor(chainId, address) {
    return this.prizeDistributors.find(function (prizeDistributor) {
      return prizeDistributor.chainId === chainId && prizeDistributor.address === address;
    });
  };

  return PrizePoolNetwork;
}();

var formatEIP2612SignatureTuple = function formatEIP2612SignatureTuple(rawSignature) {
  // NOTE: This workaround is required for hardware wallets
  // https://ethereum.stackexchange.com/questions/103307/cannot-verifiy-a-signature-produced-by-ledger-in-solidity-using-ecrecover
  var v = rawSignature.v < 27 ? rawSignature.v + 27 : rawSignature.v;
  var signatureTuple = {
    deadline: Number(rawSignature.deadline),
    v: v,
    r: rawSignature.r,
    s: rawSignature.s
  };
  return signatureTuple;
};

/**
 * A User for a PrizePool.
 * Provides read & write functionality for a Prize Pool. Reads use the provider from the PrizePool. Writes use the signer from the contructor. Throws an error if a write is triggered with a signer that does not match the network of the Prize Pool.
 * @extends PrizePool
 */

var User = /*#__PURE__*/function (_PrizePool) {
  _inheritsLoose(User, _PrizePool);

  /**
   * Creates an instance of a User for a specific PrizePool
   * @param signer signer to submit transactions with
   * @param prizePool PrizePool that the User should interact with
   */
  function User(prizePoolMetadata, signer, prizePool) {
    var _this;

    _this = _PrizePool.call(this, prizePoolMetadata, signer, prizePool.contractMetadataList) || this;
    _this.signer = signer;
    return _this;
  } //////////////////////////// Ethers write functions ////////////////////////////

  /**
   * Submits a transaction to withdraw a controlled token from the Prize Pool to the Signer.
   * @param amountUnformatted an unformatted and decimal shifted amount to withdraw from the prize pool
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */


  var _proto = User.prototype;

  _proto.withdraw =
  /*#__PURE__*/
  function () {
    var _withdraw = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee(amountUnformatted, overrides) {
      var errorPrefix, usersAddress;
      return runtime_1.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              errorPrefix = 'User [withdraw]';
              _context.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              _context.next = 5;
              return this.signer.getAddress();

            case 5:
              usersAddress = _context.sent;

              if (!Boolean(overrides)) {
                _context.next = 10;
                break;
              }

              return _context.abrupt("return", this.prizePoolContract.withdrawFrom(usersAddress, amountUnformatted, overrides));

            case 10:
              return _context.abrupt("return", this.prizePoolContract.withdrawFrom(usersAddress, amountUnformatted));

            case 11:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    }));

    function withdraw(_x, _x2) {
      return _withdraw.apply(this, arguments);
    }

    return withdraw;
  }()
  /**
   * Submits a transaction to deposit a controlled token into the Prize Pool to the Signer.
   * @param amountUnformatted an unformatted and decimal shifted amount to deposit from the prize pool
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.deposit =
  /*#__PURE__*/
  function () {
    var _deposit = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee2(amountUnformatted, overrides) {
      var errorPrefix, usersAddress;
      return runtime_1.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              errorPrefix = 'User [depositTo]';
              _context2.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              _context2.next = 5;
              return this.signer.getAddress();

            case 5:
              usersAddress = _context2.sent;

              if (!Boolean(overrides)) {
                _context2.next = 10;
                break;
              }

              return _context2.abrupt("return", this.prizePoolContract.depositTo(usersAddress, amountUnformatted, overrides));

            case 10:
              return _context2.abrupt("return", this.prizePoolContract.depositTo(usersAddress, amountUnformatted));

            case 11:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, this);
    }));

    function deposit(_x3, _x4) {
      return _deposit.apply(this, arguments);
    }

    return deposit;
  }()
  /**
   * Submits a transaction to deposit a controlled token into the Prize Pool to the Signer.
   * @param amountUnformatted an unformatted and decimal shifted amount to deposit from the prize pool
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.depositAndDelegate =
  /*#__PURE__*/
  function () {
    var _depositAndDelegate = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee3(amountUnformatted, to, overrides) {
      var errorPrefix, usersAddress;
      return runtime_1.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              errorPrefix = 'User [depositToAndDelegate]';
              _context3.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              if (!to) {
                _context3.next = 6;
                break;
              }

              _context3.next = 6;
              return validateAddress(errorPrefix, to);

            case 6:
              _context3.next = 8;
              return this.signer.getAddress();

            case 8:
              usersAddress = _context3.sent;

              if (!Boolean(overrides)) {
                _context3.next = 13;
                break;
              }

              return _context3.abrupt("return", this.prizePoolContract.depositToAndDelegate(usersAddress, amountUnformatted, to || usersAddress, overrides));

            case 13:
              return _context3.abrupt("return", this.prizePoolContract.depositToAndDelegate(usersAddress, amountUnformatted, to || usersAddress));

            case 14:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3, this);
    }));

    function depositAndDelegate(_x5, _x6, _x7) {
      return _depositAndDelegate.apply(this, arguments);
    }

    return depositAndDelegate;
  }()
  /**
   * Submits a transaction to set an allowance for deposits into the Prize Pool.
   * @param amountUnformatted an unformatted and decimal shifted amount to approve for deposits
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.approveDeposits =
  /*#__PURE__*/
  function () {
    var _approveDeposits = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee4(amountUnformatted, overrides) {
      var errorPrefix, prizePoolAddress, tokenContract;
      return runtime_1.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              errorPrefix = 'User [approveDeposits]';
              _context4.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              prizePoolAddress = this.prizePoolMetadata.address;
              _context4.next = 6;
              return this.getTokenContract();

            case 6:
              tokenContract = _context4.sent;

              if (!Boolean(overrides)) {
                _context4.next = 11;
                break;
              }

              return _context4.abrupt("return", tokenContract.approve(prizePoolAddress, amountUnformatted || MaxUint256, overrides));

            case 11:
              return _context4.abrupt("return", tokenContract.approve(prizePoolAddress, amountUnformatted || MaxUint256));

            case 12:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4, this);
    }));

    function approveDeposits(_x8, _x9) {
      return _approveDeposits.apply(this, arguments);
    }

    return approveDeposits;
  }()
  /**
   * Submits a transaction to set an allowance for deposits to 0 for the Prize Pool.
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.revokeDeposits =
  /*#__PURE__*/
  function () {
    var _revokeDeposits = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee5(overrides) {
      var errorPrefix, prizePoolAddress, tokenContract;
      return runtime_1.wrap(function _callee5$(_context5) {
        while (1) {
          switch (_context5.prev = _context5.next) {
            case 0:
              errorPrefix = 'User [revokeDeposits]';
              _context5.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              prizePoolAddress = this.prizePoolMetadata.address;
              _context5.next = 6;
              return this.getTokenContract();

            case 6:
              tokenContract = _context5.sent;

              if (!Boolean(overrides)) {
                _context5.next = 11;
                break;
              }

              return _context5.abrupt("return", tokenContract.approve(prizePoolAddress, 0, overrides));

            case 11:
              return _context5.abrupt("return", tokenContract.approve(prizePoolAddress, 0));

            case 12:
            case "end":
              return _context5.stop();
          }
        }
      }, _callee5, this);
    }));

    function revokeDeposits(_x10) {
      return _revokeDeposits.apply(this, arguments);
    }

    return revokeDeposits;
  }()
  /**
   * Submits a transaction to delegate to ticket chance to the users self
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.selfDelegateTickets =
  /*#__PURE__*/
  function () {
    var _selfDelegateTickets = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee6(overrides) {
      var errorPrefix, usersAddress;
      return runtime_1.wrap(function _callee6$(_context6) {
        while (1) {
          switch (_context6.prev = _context6.next) {
            case 0:
              errorPrefix = 'User [selfDelegateTickets]';
              _context6.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              _context6.next = 5;
              return this.signer.getAddress();

            case 5:
              usersAddress = _context6.sent;

              if (!Boolean(overrides)) {
                _context6.next = 10;
                break;
              }

              return _context6.abrupt("return", this.delegateTickets(usersAddress, overrides));

            case 10:
              return _context6.abrupt("return", this.delegateTickets(usersAddress));

            case 11:
            case "end":
              return _context6.stop();
          }
        }
      }, _callee6, this);
    }));

    function selfDelegateTickets(_x11) {
      return _selfDelegateTickets.apply(this, arguments);
    }

    return selfDelegateTickets;
  }()
  /**
   * Delegates the users ticket chance to the provided address
   * @param address the address to delegate to
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.delegateTickets =
  /*#__PURE__*/
  function () {
    var _delegateTickets = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee7(address, overrides) {
      var errorPrefix, ticketContract;
      return runtime_1.wrap(function _callee7$(_context7) {
        while (1) {
          switch (_context7.prev = _context7.next) {
            case 0:
              errorPrefix = 'User [delegateTickets]';
              _context7.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              _context7.next = 5;
              return validateAddress(errorPrefix, address);

            case 5:
              _context7.next = 7;
              return this.getTicketContract();

            case 7:
              ticketContract = _context7.sent;

              if (!Boolean(overrides)) {
                _context7.next = 12;
                break;
              }

              return _context7.abrupt("return", ticketContract.delegate(address, overrides));

            case 12:
              return _context7.abrupt("return", ticketContract.delegate(address));

            case 13:
            case "end":
              return _context7.stop();
          }
        }
      }, _callee7, this);
    }));

    function delegateTickets(_x12, _x13) {
      return _delegateTickets.apply(this, arguments);
    }

    return delegateTickets;
  }()
  /**
   * Requests a signature from the user to approve a deposit
   * @param amountUnformatted an unformatted and decimal shifted amount to approve for deposits
   * @param customDeadline a custom deadline to override the default (5 mins from signature)
   * @returns a promise to request a signature
   */
  ;

  _proto.getPermitAndDepositSignaturePromise =
  /*#__PURE__*/
  function () {
    var _getPermitAndDepositSignaturePromise = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee8(amountUnformatted, customDeadline) {
      var errorPrefix, tokenContract, tokenData, usersAddress, domain, deadline, response, nonce, signaturePromise;
      return runtime_1.wrap(function _callee8$(_context8) {
        while (1) {
          switch (_context8.prev = _context8.next) {
            case 0:
              errorPrefix = 'User [getPermitAndDepositSignaturePromise]';
              _context8.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              _context8.next = 5;
              return this.getTokenContract({
                eip2612: true
              });

            case 5:
              tokenContract = _context8.sent;
              _context8.next = 8;
              return this.getTokenData();

            case 8:
              tokenData = _context8.sent;

              if (!(!this.eip2612PermitAndDepositMetadata || !this.eip2612PermitAndDepositContract || !this.tokenMetadata || !this.signer.provider)) {
                _context8.next = 11;
                break;
              }

              throw new Error(errorPrefix + " | Error initializing contract metadata.");

            case 11:
              _context8.next = 13;
              return this.signer.getAddress();

            case 13:
              usersAddress = _context8.sent;
              domain = {
                name: tokenData.name,
                version: this.tokenMetadata.version.major.toString(),
                chainId: this.chainId,
                verifyingContract: this.tokenMetadata.address
              }; // NOTE: Nonce must be passed manually for signERC2612Permit to work with WalletConnect

              if (!(customDeadline != null)) {
                _context8.next = 19;
                break;
              }

              _context8.t0 = customDeadline;
              _context8.next = 24;
              break;

            case 19:
              _context8.next = 21;
              return this.signer.provider.getBlock('latest');

            case 21:
              _context8.t1 = _context8.sent.timestamp;
              _context8.t2 = 5 * 60;
              _context8.t0 = _context8.t1 + _context8.t2;

            case 24:
              deadline = _context8.t0;
              _context8.next = 27;
              return tokenContract.functions.nonces(usersAddress);

            case 27:
              response = _context8.sent;
              nonce = response[0];
              signaturePromise = signERC2612Permit(this.signer, domain, usersAddress, this.eip2612PermitAndDepositMetadata.address, amountUnformatted.toString(), deadline, nonce.toNumber());
              return _context8.abrupt("return", signaturePromise);

            case 31:
            case "end":
              return _context8.stop();
          }
        }
      }, _callee8, this);
    }));

    function getPermitAndDepositSignaturePromise(_x14, _x15) {
      return _getPermitAndDepositSignaturePromise.apply(this, arguments);
    }

    return getPermitAndDepositSignaturePromise;
  }()
  /**
   * Requests a signature from the user to approve a delegation
   * @param delegateTo an address to delegate to, if different from the users' wallet
   * @param customDeadline a custom deadline to override the default (5 mins from signature)
   * @returns a promise to request a signature
   */
  ;

  _proto.getPermitAndDelegateSignaturePromise =
  /*#__PURE__*/
  function () {
    var _getPermitAndDelegateSignaturePromise = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee10(delegateTo, customDeadline) {
      var errorPrefix, ticketContract, usersAddress, domain, deadline, response, nonce, signERC2612TicketPermit, signaturePromise;
      return runtime_1.wrap(function _callee10$(_context10) {
        while (1) {
          switch (_context10.prev = _context10.next) {
            case 0:
              errorPrefix = 'User [getPermitAndDelegateSignaturePromise]';
              _context10.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              _context10.next = 5;
              return this.getTicketContract();

            case 5:
              ticketContract = _context10.sent;

              if (!(!this.eip2612PermitAndDepositMetadata || !this.eip2612PermitAndDepositContract || !this.ticketMetadata || !this.signer.provider)) {
                _context10.next = 8;
                break;
              }

              throw new Error(errorPrefix + " | Error initializing contract metadata.");

            case 8:
              _context10.next = 10;
              return this.signer.getAddress();

            case 10:
              usersAddress = _context10.sent;
              // NOTE: The domain's name and version are hardcoded on the ticket contract and thus should not be changed
              domain = {
                name: 'PoolTogether ControlledToken',
                version: '1',
                chainId: this.chainId,
                verifyingContract: this.ticketMetadata.address
              }; // NOTE: Nonce must be passed manually for signERC2612Permit to work with WalletConnect

              if (!(customDeadline != null)) {
                _context10.next = 16;
                break;
              }

              _context10.t0 = customDeadline;
              _context10.next = 21;
              break;

            case 16:
              _context10.next = 18;
              return this.signer.provider.getBlock('latest');

            case 18:
              _context10.t1 = _context10.sent.timestamp;
              _context10.t2 = 5 * 60;
              _context10.t0 = _context10.t1 + _context10.t2;

            case 21:
              deadline = _context10.t0;
              _context10.next = 24;
              return ticketContract.functions.nonces(usersAddress);

            case 24:
              response = _context10.sent;
              nonce = response[0];

              signERC2612TicketPermit = /*#__PURE__*/function () {
                var _ref = _asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee9(provider, domain, user, delegate, deadline, nonce) {
                  var message, typedData, sig;
                  return runtime_1.wrap(function _callee9$(_context9) {
                    while (1) {
                      switch (_context9.prev = _context9.next) {
                        case 0:
                          message = {
                            user: user,
                            delegate: delegate,
                            nonce: nonce,
                            deadline: deadline
                          };
                          typedData = {
                            types: {
                              EIP712Domain: [{
                                name: 'name',
                                type: 'string'
                              }, {
                                name: 'version',
                                type: 'string'
                              }, {
                                name: 'chainId',
                                type: 'uint256'
                              }, {
                                name: 'verifyingContract',
                                type: 'address'
                              }],
                              Delegate: [{
                                name: 'user',
                                type: 'address'
                              }, {
                                name: 'delegate',
                                type: 'address'
                              }, {
                                name: 'nonce',
                                type: 'uint256'
                              }, {
                                name: 'deadline',
                                type: 'uint256'
                              }]
                            },
                            primaryType: 'Delegate',
                            domain: domain,
                            message: message
                          };
                          _context9.next = 4;
                          return signData(provider, user, typedData);

                        case 4:
                          sig = _context9.sent;
                          return _context9.abrupt("return", _extends({}, sig, message));

                        case 6:
                        case "end":
                          return _context9.stop();
                      }
                    }
                  }, _callee9);
                }));

                return function signERC2612TicketPermit(_x18, _x19, _x20, _x21, _x22, _x23) {
                  return _ref.apply(this, arguments);
                };
              }();

              signaturePromise = signERC2612TicketPermit(this.signer, domain, usersAddress, delegateTo || usersAddress, deadline, nonce.toNumber());
              return _context10.abrupt("return", signaturePromise);

            case 29:
            case "end":
              return _context10.stop();
          }
        }
      }, _callee10, this);
    }));

    function getPermitAndDelegateSignaturePromise(_x16, _x17) {
      return _getPermitAndDelegateSignaturePromise.apply(this, arguments);
    }

    return getPermitAndDelegateSignaturePromise;
  }()
  /**
   * Submits a transaction to deposit and delegate a token into the prize pool using signature approvals.
   * @param amountUnformatted an unformatted and decimal shifted amount to deposit into the prize pool
   * @param permitSignature an EIP2612 signature to approve the token deposit
   * @param delegateSignature an EIP2612 signature to approve the token delegation
   * @param to optional wallet address to delegate to (if empty, delegates to same user)
   * @param overrides optional overrides for the transaction creation
   * @returns the transaction response
   */
  ;

  _proto.depositAndDelegateWithSignature =
  /*#__PURE__*/
  function () {
    var _depositAndDelegateWithSignature = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee11(amountUnformatted, permitSignature, delegateSignature, to, overrides) {
      var errorPrefix, usersAddress, formattedPermitSignature, formattedDelegateSignature;
      return runtime_1.wrap(function _callee11$(_context11) {
        while (1) {
          switch (_context11.prev = _context11.next) {
            case 0:
              errorPrefix = 'User [depositToAndDelegateWithSignature]';
              _context11.next = 3;
              return this.validateSignerNetwork(errorPrefix);

            case 3:
              if (!to) {
                _context11.next = 6;
                break;
              }

              _context11.next = 6;
              return validateAddress(errorPrefix, to);

            case 6:
              if (this.eip2612PermitAndDepositContract) {
                _context11.next = 8;
                break;
              }

              throw new Error(errorPrefix + " | Error initializing contract.");

            case 8:
              _context11.next = 10;
              return this.signer.getAddress();

            case 10:
              usersAddress = _context11.sent;
              formattedPermitSignature = formatEIP2612SignatureTuple(permitSignature);
              formattedDelegateSignature = {
                delegate: to || usersAddress,
                signature: formatEIP2612SignatureTuple(delegateSignature)
              };

              if (!Boolean(overrides)) {
                _context11.next = 17;
                break;
              }

              return _context11.abrupt("return", this.eip2612PermitAndDepositContract.permitAndDepositToAndDelegate(this.address, amountUnformatted, to || usersAddress, formattedPermitSignature, formattedDelegateSignature, overrides));

            case 17:
              return _context11.abrupt("return", this.eip2612PermitAndDepositContract.permitAndDepositToAndDelegate(this.address, amountUnformatted, to || usersAddress, formattedPermitSignature, formattedDelegateSignature));

            case 18:
            case "end":
              return _context11.stop();
          }
        }
      }, _callee11, this);
    }));

    function depositAndDelegateWithSignature(_x24, _x25, _x26, _x27, _x28) {
      return _depositAndDelegateWithSignature.apply(this, arguments);
    }

    return depositAndDelegateWithSignature;
  }() //////////////////////////// Ethers read functions ////////////////////////////

  /**
   * Fetches the Users ticket balance.
   * @returns the users ticket balance
   */
  ;

  _proto.getTicketBalance =
  /*#__PURE__*/
  function () {
    var _getTicketBalance = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee12() {
      var usersAddress;
      return runtime_1.wrap(function _callee12$(_context12) {
        while (1) {
          switch (_context12.prev = _context12.next) {
            case 0:
              _context12.next = 2;
              return this.signer.getAddress();

            case 2:
              usersAddress = _context12.sent;
              return _context12.abrupt("return", this.getUsersTicketBalance(usersAddress));

            case 4:
            case "end":
              return _context12.stop();
          }
        }
      }, _callee12, this);
    }));

    function getTicketBalance() {
      return _getTicketBalance.apply(this, arguments);
    }

    return getTicketBalance;
  }()
  /**
   * Fetches the Users token (underlying token) balance.
   * @returns the users underlying token balance
   */
  ;

  _proto.getTokenBalance =
  /*#__PURE__*/
  function () {
    var _getTokenBalance = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee13() {
      var usersAddress;
      return runtime_1.wrap(function _callee13$(_context13) {
        while (1) {
          switch (_context13.prev = _context13.next) {
            case 0:
              _context13.next = 2;
              return this.signer.getAddress();

            case 2:
              usersAddress = _context13.sent;
              return _context13.abrupt("return", this.getUsersTokenBalance(usersAddress));

            case 4:
            case "end":
              return _context13.stop();
          }
        }
      }, _callee13, this);
    }));

    function getTokenBalance() {
      return _getTokenBalance.apply(this, arguments);
    }

    return getTokenBalance;
  }()
  /**
   * Fetches the allowance the User has for depositing into the Prize Pool.
   * @returns the allowance the user has set for deposits
   */
  ;

  _proto.getDepositAllowance =
  /*#__PURE__*/
  function () {
    var _getDepositAllowance = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee14() {
      var usersAddress;
      return runtime_1.wrap(function _callee14$(_context14) {
        while (1) {
          switch (_context14.prev = _context14.next) {
            case 0:
              _context14.next = 2;
              return this.signer.getAddress();

            case 2:
              usersAddress = _context14.sent;
              return _context14.abrupt("return", this.getUsersDepositAllowance(usersAddress));

            case 4:
            case "end":
              return _context14.stop();
          }
        }
      }, _callee14, this);
    }));

    function getDepositAllowance() {
      return _getDepositAllowance.apply(this, arguments);
    }

    return getDepositAllowance;
  }()
  /**
   * Fetches the address the user has delegated to
   * @returns the address the user has delegated to
   */
  ;

  _proto.getTicketDelegate =
  /*#__PURE__*/
  function () {
    var _getTicketDelegate = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee15() {
      var usersAddress;
      return runtime_1.wrap(function _callee15$(_context15) {
        while (1) {
          switch (_context15.prev = _context15.next) {
            case 0:
              _context15.next = 2;
              return this.signer.getAddress();

            case 2:
              usersAddress = _context15.sent;
              return _context15.abrupt("return", this.getUsersTicketDelegate(usersAddress));

            case 4:
            case "end":
              return _context15.stop();
          }
        }
      }, _callee15, this);
    }));

    function getTicketDelegate() {
      return _getTicketDelegate.apply(this, arguments);
    }

    return getTicketDelegate;
  }() //////////////////////////// Validation methods ////////////////////////////

  /**
   * Validates the provided signers network.
   * Throws if it does not match the expected network.
   * @param errorPrefix the class and function name of where the error occurred
   */
  ;

  _proto.validateSignerNetwork =
  /*#__PURE__*/
  function () {
    var _validateSignerNetwork2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/runtime_1.mark(function _callee16(errorPrefix) {
      return runtime_1.wrap(function _callee16$(_context16) {
        while (1) {
          switch (_context16.prev = _context16.next) {
            case 0:
              validateSignerNetwork(errorPrefix, this.signer, this.chainId);

            case 1:
            case "end":
              return _context16.stop();
          }
        }
      }, _callee16, this);
    }));

    function validateSignerNetwork$1(_x29) {
      return _validateSignerNetwork2.apply(this, arguments);
    }

    return validateSignerNetwork$1;
  }();

  return User;
}(PrizePool);

export { ContractFactory, ContractType, DAYS_IN_SECONDS, HOUR_IN_SECONDS, PrizeApi, PrizeApiStatus, PrizeDistributor, PrizePool, PrizePoolNetwork, TIER_DENOMINATION, User, createContract, createContractMetadata, createInterface, formatTierToBasePercentage, getContractListChainIds, getContractsByType, getMetadataAndContract, getTokenData, getUsersERC20Balance, getUsersTokenAllowance, initializePrizeDistributors, initializePrizePools, sortContractsByChainId, sortContractsByContractTypeAndChildren, validateAddress, validateIsSigner, validateSignerNetwork, validateSignerOrProviderNetwork };
//# sourceMappingURL=v4-client-js.esm.js.map
